import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from "uuid";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import { loadPincodeDb } from '../utils/pincodeDb';
import {
  User as UserIcon, Phone as PhoneIcon, Mail as MailIcon, MapPin as MapPinIcon, Store as StoreIcon, Package as PackageIcon,
  Truck as TruckIcon, IndianRupee as RupeeIcon, Tag as TagIcon, Box as BoxIcon, Check as CheckIcon, Sparkles as SparklesIcon,
  Search as SearchIcon, RefreshCw as RefreshIcon, Plus as PlusIcon, X as XIcon, ArrowRight, ArrowLeft, FileText as FileTextIcon,
  Pencil as PencilIcon, Trash2 as TrashIcon, MoreVertical as MoreVerticalIcon, ChevronDown as ChevronDownIcon, Lock as LockIcon,
  RotateCcw as ReturnIcon, ScanLine as ScanIcon, SearchX as SearchXIcon, ExternalLink as ExternalLinkIcon, Copy as CopyIcon,
  AlertTriangle as AlertTriangleIcon, Ban as BanIcon,
} from "lucide-react";
import { API } from '../utils/apiUrls';
import { deepTrimStrings } from '../utils/helpers';
import { getOrderStatusName } from '../utils/enums';
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchWalletBalance } from '../redux/slices/walletSlice';
import PickupAddressModal, { deletePickupAddress, DeleteConfirmModal } from '../components/PickupAddressModal';
import { useOnboardingGate } from '../components/onboarding/OnboardingGate';
import OrderCreationIssueModal from '../components/orders/OrderCreationIssueModal';
import { interpretOrderCreationError, interpretOrderCreationResponse } from '../utils/orderCreationError';
import { CARD, EMAIL_RE, titleCaseLoc, displayLocName, locInitials, LABEL, SPRING, inputClass, BTN_PRIMARY, BTN_OUTLINE, BTN_GHOST, numericKeyGuard, numericPasteGuard, fmtInr, initialsOf, formatCarrier, carrierAccent, estDeliveryDate, WEEKDAY_ABBR, MONTH_ABBR, ASSUMED_TAT_DAYS } from '../components/createorder/tokens';
import { assessReturnEligibility } from '../components/createorder/returnEligibility';
import { readLayoutPreference, writeLayoutPreference, FormLayoutCard } from '../components/createorder/layoutPreference';
import JourneyGroup, { FieldError } from '../components/createorder/JourneyGroup';
import ShipmentTicket from '../components/createorder/ShipmentTicket';
import BookedSuccess from '../components/createorder/BookedSuccess';
import PackageStudio from '../components/createorder/PackageStudio';
import SmartAllocationEngine from '../components/createorder/SmartAllocationEngine';
import CourierPicker from '../components/createorder/CourierPicker';
import { useStepRowAutoScroll, useMoreBelowCue, useNumberWheelBlur } from '../components/createorder/hooks';

/* ─────────────────────────────────────────────────────────────────────────────
   The reverse desk: the same desk as a forward order, driven the other way. On
   the left the merchant says who is sending the parcel back and where it should
   come back to; on the right the return's ticket fills in live — its truck
   drives right → left, because this one is coming home. Booking stamps it.

   Every API call, payload and rule below is the reverse page's existing logic;
   only the presentation is new. State keeps the reverse page's own names:
   `pickupDetails` is the customer (the courier collects from them) and
   `dropDetails` / `selectedDrop` are the merchant's saved locations (the return
   is delivered there).
──────────────────────────────────────────────────────────────────────────── */
const FIELD_MSG = {
  phoneNumber: "Must be 10 digits — drop the +91 or leading 0",
  name: "Add the customer's name",
  email: "Enter a valid email address",
  drop: "Choose a return location to continue",
  address: "Add the full pickup address — at least 10 characters",
  city: "Add the city",
  state: "Add the state",
  pincode: "Enter the 6-digit pincode",
  clientOrderId: "Add an order reference, or hit Generate",
  invoiceValue: "Enter an invoice value above ₹0",
  "item-name": "Add the item name",
  "item-skuId": "Add a SKU, or generate one",
  "item-quantity": "Quantity must be a whole number, 1 or more",
  "item-unitPrice": "Add the unit price",
  weight: "Enter a weight above 0, up to 200 kg",
  length: "Enter a size above 0, up to 500 cm",
  breadth: "Enter a size above 0, up to 500 cm",
  height: "Enter a size above 0, up to 500 cm",
};
const FIELD_STEP = { phoneNumber: 1, name: 1, email: 1, address: 1, city: 1, state: 1, pincode: 1, drop: 2, clientOrderId: 3, invoiceValue: 3, weight: 4, length: 4, breadth: 4, height: 4 };

/* Order-reference precheck outcomes that must block booking. OMS treats a create request whose
   reference already has a placed order — cancelled or not — as that same order and answers
   "Order Placed Successfully" without creating anything, so booking would silently do nothing.
   FAILED is deliberately not here: booking again retries that order with the new details. */
const REF_BLOCKING_OUTCOMES = new Set(["PLACED", "PROCESSING", "CONFIRMATION_PENDING", "UNKNOWN"]);

/* The lookup's verdict colours, keyed by the eligibility bucket (see returnEligibility.js). */
const STATUS_CHIP = {
  delivered: "bg-emerald-50 text-emerald-700 ring-emerald-200", undelivered: "bg-[#3877FC]/[0.08] text-[#2A59BD] ring-[#3877FC]/20",
  cancelled: "bg-slate-100 text-slate-600 ring-slate-200", rto: "bg-amber-50 text-amber-700 ring-amber-200",
  lost: "bg-rose-50 text-rose-700 ring-rose-200", damaged: "bg-rose-50 text-rose-700 ring-rose-200",
  exchange: "bg-violet-50 text-violet-700 ring-violet-200", reverse: "bg-slate-100 text-slate-600 ring-slate-200", unclear: "bg-slate-100 text-slate-600 ring-slate-200",
};
const fmtDayTime = (iso) => {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return null;
  let h = d.getHours();
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${WEEKDAY_ABBR[d.getDay()]}, ${d.getDate()} ${MONTH_ABBR[d.getMonth()]} · ${h}:${String(d.getMinutes()).padStart(2, "0")} ${ap}`;
};
const daysSince = (iso) => {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};
const agoLabel = (n) => (n == null ? null : n === 0 ? "today" : n === 1 ? "yesterday" : `${n} days ago`);
const titleCaseCity = (s) => String(s || "").trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/* One end of the original route: who/where, then city and the mono pincode. */
function StubEndpoint({ label, name, city, pin, align = "left" }) {
  return (
    <div className={`min-w-0 ${align === "right" ? "text-right" : ""}`}>
      <div className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-slate-400">{label}</div>
      <div className="mt-0.5 line-clamp-2 text-[13px] font-semibold leading-snug text-slate-900">{displayLocName(name) || "—"}</div>
      <div className="mt-0.5 truncate text-[12px] text-slate-500">{titleCaseCity(city)}{city && pin ? " · " : ""}<span className="font-mono tabular-nums text-slate-600">{pin}</span></div>
    </div>
  );
}
function StubFact({ label, children, sub }) {
  return (
    <div className="min-w-0">
      <div className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-slate-400">{label}</div>
      <div className="mt-0.5 whitespace-nowrap text-[13px] font-semibold leading-snug text-slate-900">{children}</div>
      {sub ? <div className="mt-0.5 whitespace-nowrap text-[11px] font-medium text-slate-400">{sub}</div> : null}
    </div>
  );
}
/* Copy-to-clipboard beside an identifier — the shipment page's control, same feedback (check for 1.4s). */
function CopyChip({ value, label }) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard?.writeText(String(value));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      title={`Copy ${label}`}
      aria-label={`Copy ${label}`}
      className={`grid h-6 w-6 flex-shrink-0 place-items-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 ${copied ? "text-emerald-600" : "text-slate-400 hover:bg-white hover:text-[#2A59BD] hover:shadow-[inset_0_0_0_1px_#D6DEE9]"}`}
    >
      {copied ? <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} /> : <CopyIcon className="h-3.5 w-3.5" strokeWidth={2.25} />}
    </button>
  );
}
/* A one-line notice inside the verdict band: the icon sits beside a single inline-flow span, so
   mono fragments inside the sentence wrap like text, not like flex items. */
function StubNotice({ children }) {
  return (
    <p className="mt-1 flex items-start gap-1.5 text-[12px] font-medium leading-relaxed text-amber-700">
      <AlertTriangleIcon className="mt-[3px] h-3.5 w-3.5 flex-shrink-0" />
      <span className="min-w-0">{children}</span>
    </p>
  );
}
/* Status names come as enum-ish strings ("RTO_IN_TRANSIT"); the UI enum has the readable name. */
const readableStatus = (code, raw) => {
  const known = getOrderStatusName(code);
  if (known && known !== "Unknown Status") return known;
  return String(raw || "Unknown status").toLowerCase().replace(/_/g, " ").replace(/\b(rto|ndr|edd|cod)\b/g, (m) => m.toUpperCase()).replace(/^\w/, (c) => c.toUpperCase());
};

/* The original shipment, as a compact stub in the ticket language: AWB and status up top, the
   forward route, the facts that matter for a return (placed, delivered, courier, what it cost,
   what was paid), and the verdict band — eligible, or why not. */
function OriginalShipmentStub({ lookup, onClear }) {
  const { status, awb, order, eligibility } = lookup;
  if (status === "searching") {
    return (
      <div className="rounded-xl border border-[#D6DEE9] bg-white px-4 py-3.5" role="status" aria-label="Looking up the shipment">
        <div className="flex items-center justify-between"><div className="cfo-skeleton h-3.5 w-40 rounded" /><div className="cfo-skeleton h-5 w-24 rounded-full" /></div>
        <div className="mt-3.5 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3"><div className="cfo-skeleton h-8 w-28 rounded" /><div className="cfo-skeleton h-2 w-10 rounded" /><div className="cfo-skeleton ml-auto h-8 w-28 rounded" /></div>
        <p className="mt-3 text-[12px] font-medium text-slate-500">Looking up <span className="font-mono text-slate-700">{awb}</span> on Wayuvega…</p>
      </div>
    );
  }
  if (status === "notfound" || status === "error") {
    const err = status === "error";
    return (
      <div className={`flex items-start gap-3 rounded-xl border px-4 py-3.5 ${err ? "border-amber-200 bg-amber-50/70" : "border-rose-200 bg-rose-50/70"}`} role="status">
        <span className={`mt-0.5 grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg ${err ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-600"}`}><SearchXIcon className="h-4 w-4" strokeWidth={2.25} /></span>
        <div className="min-w-0">
          <p className={`text-[13.5px] font-semibold ${err ? "text-amber-900" : "text-rose-800"}`}>
            {err ? "Couldn't look up that AWB right now." : <>No shipment with AWB <span className="font-mono">{awb}</span> on your Wayuvega account.</>}
          </p>
          <p className={`mt-1 text-[12.5px] leading-relaxed ${err ? "text-amber-800/80" : "text-rose-700/85"}`}>
            {err ? "Try again in a moment, or fill the return below — it goes ahead as usual." : "Only shipments booked on Wayuvega under this account can be found here. Fill the return below and it goes ahead as usual."}
          </p>
        </div>
      </div>
    );
  }
  if (status !== "found" || !order) return null;
  const ok = Boolean(eligibility?.ok);
  const code = String(order.latest_order_status_code ?? "");
  const statusName = ok ? "Delivered" : readableStatus(code, order.latest_order_status);
  const latestTs = order.latest_status_timestamp;
  const placedTs = order.order_placed_date_time || order.shipment_order_date_time_utc || order.order_date_time;
  const days = daysSince(latestTs);
  const { carrier, mode } = formatCarrier(order.allocated_carrier_partner_public_name || "");
  const accent = carrierAccent(carrier);
  const pay = order.payment_details || {};
  const isCod = String(pay.payment_mode || "").toUpperCase() === "COD";
  const itemsCount = (order.shipment_packages || []).reduce((n, p) => n + ((p.items || []).length), 0);
  const chip = STATUS_CHIP[eligibility?.kind] || STATUS_CHIP.unclear;
  const detailsHref = order.order_reference_number ? `/shipment-details/${encodeURIComponent(order.order_reference_number)}` : null;
  return (
    <div className="overflow-hidden rounded-xl border border-[#D6DEE9] bg-white shadow-[0_1px_2px_rgba(42,89,189,0.05)]">
      {/* head: the AWB, and only the AWB */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 border-b border-[#E7ECF3] bg-[#3877FC]/[0.04] px-4 py-2.5">
        <div className="min-w-0">
          <div className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-slate-500">Original shipment</div>
          <div className="mt-0.5 flex items-center gap-1">
            <span className="font-mono text-[16px] font-bold tracking-[0.05em] text-slate-900">{order.awb_number || awb}</span>
            <CopyChip value={order.awb_number || awb} label="AWB" />
          </div>
        </div>
        <span className="rounded-md bg-white px-2 py-1 text-[10.5px] font-bold uppercase tracking-[0.1em] text-slate-500 ring-1 ring-inset ring-[#E2EAF8]">
          {String(order.shipment_order_type || "").toUpperCase() === "RVP" ? "Return" : "Forward"}
        </span>
      </div>
      {/* identity: the merchant's own reference, and where the shipment stands */}
      <div className="grid gap-x-8 gap-y-3 border-b border-[#E7ECF3] px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
        <div className="min-w-0">
          <div className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-slate-400">Order reference</div>
          {order.order_reference_number ? (
            <div className="mt-0.5 flex items-start gap-1">
              <span className="break-all font-mono text-[12.5px] font-semibold leading-6 text-slate-800">{order.order_reference_number}</span>
              <CopyChip value={order.order_reference_number} label="order reference" />
            </div>
          ) : <div className="mt-0.5 text-[13px] font-semibold text-slate-900">—</div>}
        </div>
        <div className="min-w-0">
          <div className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-slate-400">Status</div>
          <div className="mt-1">
            <span className={`inline-flex max-w-full items-start gap-1.5 rounded-full px-2.5 py-1 text-left text-[11px] font-bold leading-4 ring-1 ring-inset sm:items-center sm:whitespace-nowrap ${chip}`}>
              <span className={`mt-[5px] h-1.5 w-1.5 flex-shrink-0 rounded-full sm:mt-0 ${ok ? "bg-emerald-500" : "bg-current opacity-60"}`} aria-hidden="true" />
              <span>{statusName}{days != null ? <span className="font-medium opacity-75"> · {agoLabel(days)}</span> : null}</span>
            </span>
          </div>
        </div>
      </div>
      {/* the forward route */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 px-4 py-3">
        <StubEndpoint label="From" name={order.pickup_organisation || order.pickup_name} city={order.pickup_city} pin={order.pickup_pincode} />
        <span className="grid h-7 w-7 place-items-center rounded-full bg-[#3877FC]/[0.08] text-[#2A59BD]" aria-hidden="true"><ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} /></span>
        <StubEndpoint label="To" name={order.drop_name} city={order.drop_city} pin={order.drop_pincode} align="right" />
      </div>
      {/* the facts a return depends on — natural widths, wrapping into rows as space allows */}
      <div className="flex flex-wrap gap-x-7 gap-y-3 border-t border-[#E7ECF3] px-4 py-3">
        <StubFact label="Placed">{fmtDayTime(placedTs) || "—"}</StubFact>
        <StubFact label={ok ? "Delivered" : "Last update"}>{fmtDayTime(latestTs) || "—"}</StubFact>
        <StubFact label="Courier" sub={mode || null}>
          <span className="inline-flex items-center gap-1.5">
            <span className="grid h-5 w-5 place-items-center rounded-md text-[9px] font-extrabold" style={{ backgroundImage: `linear-gradient(150deg, ${accent}24, ${accent}0C)`, color: accent, boxShadow: `inset 0 0 0 1px ${accent}2E` }}>{initialsOf(carrier || "?")}</span>
            <span>{carrier || "—"}</span>
          </span>
        </StubFact>
        <StubFact label="Shipment charge" sub={order.total_shipping_tax ? "incl. GST" : null}>{order.total_shipping_charge != null ? fmtInr(order.total_shipping_charge) : "—"}</StubFact>
        <StubFact label="Order value" sub={[isCod ? "Cash on delivery" : "Prepaid", itemsCount ? `${itemsCount} item${itemsCount === 1 ? "" : "s"}` : null].filter(Boolean).join(" · ")}>{pay.total_amount != null ? fmtInr(pay.total_amount) : "—"}</StubFact>
      </div>
      {/* verdict */}
      <div className={`flex flex-col items-start gap-x-4 gap-y-2 border-t px-4 py-3 sm:flex-row ${ok ? "border-emerald-100 bg-emerald-50/70" : "border-amber-200/80 bg-amber-50/80"}`}>
        <div className="flex min-w-0 items-start gap-2.5 sm:flex-1">
          <span className={`mt-px grid h-5 w-5 flex-shrink-0 place-items-center rounded-full ${ok ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"}`}>
            {ok ? <CheckIcon className="h-3 w-3" strokeWidth={3.5} /> : <BanIcon className="h-3 w-3" strokeWidth={2.75} />}
          </span>
          <div className="min-w-0">
            <p className={`text-[13px] font-semibold ${ok ? "text-emerald-900" : "text-amber-900"}`}>
              {ok ? "Eligible for a return — customer, return location, items and package filled in below." : "Can't start a return from this shipment"}
            </p>
            {!ok && <p className="mt-0.5 text-[12.5px] leading-relaxed text-amber-800/90">{eligibility?.reason} <span className="text-amber-800/70">The form stays locked for this shipment — clear the AWB to book a different return.</span></p>}
            {ok && days != null && days > 30 && (
              <StubNotice>Delivered {agoLabel(days)} — check it's still within your return window.</StubNotice>
            )}
          </div>
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2 pl-[30px] sm:pl-0">
          {!ok && onClear && (
            <button type="button" onClick={onClear} className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg bg-white px-3 text-[12px] font-semibold text-amber-800 ring-1 ring-inset ring-amber-300 transition-colors hover:bg-amber-100/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400">
              <XIcon className="h-3.5 w-3.5" strokeWidth={2.5} /> Clear AWB
            </button>
          )}
          {detailsHref && (
            <a href={detailsHref} target="_blank" rel="noopener noreferrer" className={`inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg bg-white px-3 text-[12px] font-semibold ring-1 ring-inset transition-colors focus-visible:outline-none focus-visible:ring-2 ${ok ? "text-emerald-800 ring-emerald-300 hover:bg-emerald-100/60 focus-visible:ring-emerald-400" : "text-amber-800 ring-amber-300 hover:bg-amber-100/70 focus-visible:ring-amber-400"}`}>
              <ExternalLinkIcon className="h-3.5 w-3.5" strokeWidth={2.25} /> View original shipment
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

const CreateReverseOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { requireBusinessOps } = useOnboardingGate();
  const dispatch = useDispatch();
  const walletType = useSelector((s) => s.wallet?.type); // fallback settlement source (redux wallet type)
  const [resolvedSettlement, setResolvedSettlement] = useState(null); // authoritative settlement type from OMS
  const [draftOrderId, setDraftOrderId] = useState(location?.state?.draftOrderId || null);
  const [step, setStep] = useState(1);
  const [layoutMode, setLayoutMode] = useState(readLayoutPreference); // "horizontal" | "vertical" — shared with the forward desk
  const [pendingDraftLanding, setPendingDraftLanding] = useState(false); // clone hydrated → place the stepper from its data
  const stacked = layoutMode === "vertical";
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSuccessModal, setDraftSuccessModal] = useState({ open: false, draftId: null, isUpdate: false });
  const [orderIssue, setOrderIssue] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [bookedAt, setBookedAt] = useState(null); // stamped moment of booking (client clock)
  const [loadingPincode, setLoadingPincode] = useState(false);
  const [pincodeLookupFailed, setPincodeLookupFailed] = useState(false);
  const [courierServiceability, setCourierServiceability] = useState([]);
  const [customerLookupResults, setCustomerLookupResults] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [awbNumber, setAwbNumber] = useState("");
  // The "start from a shipment" lookup: idle → searching → found (with the eligibility verdict) |
  // notfound | error. The stub only shows while the typed AWB is the one that was looked up.
  const [awbLookup, setAwbLookup] = useState({ status: "idle", awb: "", order: null, eligibility: null });
  const searchingAwb = awbLookup.status === "searching";
  const lookupCurrent = awbLookup.status !== "idle" && awbLookup.awb === String(awbNumber || "").trim().toUpperCase();
  const awbFetched = lookupCurrent && awbLookup.status === "found" && Boolean(awbLookup.eligibility?.ok);
  // A refused lookup locks the desk: that shipment can't be returned, so the form below must not
  // quietly become a manual return for it. Clearing or changing the AWB unlocks everything.
  const lookupRefused = lookupCurrent && awbLookup.status === "found" && Boolean(awbLookup.eligibility) && !awbLookup.eligibility.ok;
  const clearAwbLookup = () => { setAwbNumber(""); setAwbLookup({ status: "idle", awb: "", order: null, eligibility: null }); };

  const [formData, setFormData] = useState({
    pickupDetails: { name: "", phoneNumber: "", email: "", address: "", city: "", state: "", pincode: "" },
    orderDetails: {
      clientOrderId: "",
      invoiceValue: 0,
      itemDetails: [{ name: "", quantity: 1, unitPrice: "", skuId: "", hsnCode: "" }],
    },
    packageDetails: { weight: "", length: "", breadth: "", height: "" },
    courierDetails: { courierPartner: "", useRecommendation: true },
    paymentMode: "Prepaid",
  });

  const [dropDetails, setDropDetails] = useState([]);
  const [selectedDrop, setSelectedDrop] = useState(null);
  const selectedDropRef = useRef(null);
  selectedDropRef.current = selectedDrop;
  /* A return location that came from the AWB lookup carries no saved id. Whenever the saved list
     (re)loads, match it to a saved location — pincode plus phone, or pincode plus address — so
     that card shows selected; if nothing matches, keep it in the list as its own entry so the
     choice stays visible instead of silently vanishing. */
  const reconcileFetchedDrop = (list) => {
    const cur = selectedDropRef.current;
    if (!cur || cur.pickup_address_id != null) return list;
    const pin = String(cur.pickup_postal_code || "").trim();
    const phone = String(cur.pickup_poc_contact_number || "").trim();
    const addr = String(cur.pickup_address || "").trim().toLowerCase();
    const match = list.find((a) => String(a.pickup_postal_code || "").trim() === pin && (
      (phone && String(a.pickup_poc_contact_number || "").trim() === phone) ||
      (addr && String(a.pickup_address || "").trim().toLowerCase().includes(addr.slice(0, 30)))
    ));
    if (match) { setSelectedDrop(match); return list; }
    return [{ ...cur, __fromOrder: true }, ...list];
  };
  const [showShippingDetails, setShowShippingDetails] = useState(false);
  const [courierOptions, setCourierOptions] = useState([]);
  const [loadingCouriers, setLoadingCouriers] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [units, setUnits] = useState({ weight: 'gm', length: 'cm', breadth: 'cm', height: 'cm' });
  const cloneFrom = location?.state?.cloneFrom;
  const cloneMode = Boolean(cloneFrom);

  /* Order-reference precheck: as the reference changes (typed, or suggested by the quick-fill),
     ask OMS whether an order already exists for it (read-only) and block before the merchant
     fills and books the whole form. Fails open. Skipped in clone / retry mode. */
  const [orderRefCheck, setOrderRefCheck] = useState({ ref: "", outcome: "NONE", awb: null });
  const orderRefSeq = useRef(0);
  const orderRefTimer = useRef(null);
  useEffect(() => {
    const ref = String(formData.orderDetails.clientOrderId || "").trim();
    const seq = ++orderRefSeq.current; // invalidate any in-flight response
    if (orderRefTimer.current) clearTimeout(orderRefTimer.current);
    if (cloneMode || !ref) { setOrderRefCheck({ ref: "", outcome: "NONE", awb: null }); return; }
    orderRefTimer.current = setTimeout(() => {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      axios.get(API.orderReferencePrecheck(ref), { headers: { Authorization: authToken, "x-merchant-id": merchantId }, validateStatus: () => true })
        .then((resp) => {
          if (seq !== orderRefSeq.current) return;
          if (resp.status >= 200 && resp.status < 300 && resp.data && resp.data.exists) {
            setOrderRefCheck({ ref, outcome: String(resp.data.outcome || "UNKNOWN").toUpperCase(), awb: resp.data.awbNumber || null });
          } else {
            setOrderRefCheck({ ref, outcome: "NONE", awb: null }); // non-exists or any error → allow
          }
        })
        .catch(() => { if (seq === orderRefSeq.current) setOrderRefCheck({ ref, outcome: "NONE", awb: null }); });
    }, 450);
    return () => { if (orderRefTimer.current) clearTimeout(orderRefTimer.current); };
  }, [formData.orderDetails.clientOrderId, cloneMode]);
  const refBlockedFor = (ref) => !cloneMode && ref !== "" && orderRefCheck.ref === ref && REF_BLOCKING_OUTCOMES.has(orderRefCheck.outcome);
  const [highestStepReached, setHighestStepReached] = useState(1);
  const [expandedHSNItems, setExpandedHSNItems] = useState({});

  // Address management state (the merchant's saved locations double as return locations)
  const [addrModalOpen, setAddrModalOpen] = useState(false);
  const [addrModalMode, setAddrModalMode] = useState("add");
  const [addrModalLocation, setAddrModalLocation] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pickupToDelete, setPickupToDelete] = useState(null);
  const [deletingPickup, setDeletingPickup] = useState(false);

  // Presentation-only state: never touches the payload.
  const [dropQuery, setDropQuery] = useState("");
  const [dropMenuFor, setDropMenuFor] = useState(null);
  const [refreshingDrops, setRefreshingDrops] = useState(false);
  const [showUnserviceable, setShowUnserviceable] = useState(false);
  const [courierSort, setCourierSort] = useState("cheapest");
  const [awbCopied, setAwbCopied] = useState(false);
  const dropListRef = useRef(null);
  const dropListRO = useRef(null);
  const stepRowRef = useRef(null);
  const stepBodyRef = useRef(null);
  const footerRef = useRef(null);
  const { moreBelow, scrollForMore, moreBelowComputeRef } = useMoreBelowCue({ stepBodyRef, footerRef, step });
  useStepRowAutoScroll(stepRowRef, step);
  useNumberWheelBlur();
  const [dropEdges, setDropEdges] = useState({ top: true, bottom: true });
  const updateDropEdges = () => {
    const el = dropListRef.current;
    if (!el) return;
    const top = el.scrollTop <= 2;
    const bottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    setDropEdges((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
    moreBelowComputeRef.current();
  };

  const resetForm = () => {
    setFormData({
      pickupDetails: { name: "", phoneNumber: "", email: "", address: "", city: "", state: "", pincode: "" },
      orderDetails: { clientOrderId: "", invoiceValue: 0, itemDetails: [{ name: "", quantity: 1, unitPrice: "", skuId: "", hsnCode: "" }] },
      packageDetails: { weight: "", length: "", breadth: "", height: "" },
      courierDetails: { courierPartner: "", useRecommendation: true },
      paymentMode: "Prepaid",
    });
    setStep(1);
    setHighestStepReached(1);
    setResponseData(null);
    setSelectedDrop(null);
    setAwbNumber("");
    setAwbLookup({ status: "idle", awb: "", order: null, eligibility: null });
    setErrors({});
    setTouched({});
    setExpandedHSNItems({});
  };

  // Pre-fill from an existing RVP order (clone / draft), exactly as before
  useEffect(() => {
    if (!cloneFrom) return;

    const allItems = (cloneFrom.shipment_packages || []).flatMap(
      (pkg) => (pkg.items || []).map((item) => ({
        name: item.item_general_details?.item_name || "",
        skuId: item.item_general_details?.sku || "",
        quantity: item.quantity || 1,
        unitPrice: item.item_payment_details?.unit_price || "",
        hsnCode: item.item_general_details?.hsn_code || "",
      }))
    );
    const itemDetails = allItems.length > 0
      ? allItems
      : [{ name: "", skuId: "", quantity: 1, unitPrice: "", hsnCode: "" }];

    const packageDims = cloneFrom.shipment_package_dimensions || {};
    const dimensionUnit = packageDims.unit || "cm";

    const baseRef = cloneFrom.order_reference_number || "";
    const clientOrderId = baseRef.replace(/(_CLONE|_retry)$/i, "") + "_CLONE";

    const paymentMode = (cloneFrom.payment_details?.payment_mode || "").toUpperCase() === "COD"
      ? "COD"
      : "Prepaid";

    setFormData((prev) => ({
      ...prev,
      pickupDetails: {
        name: cloneFrom.pickup_name || "",
        phoneNumber: cloneFrom.pickup_poc_contact_number || "",
        email: cloneFrom.pickup_poc_email || "",
        address: cloneFrom.pickup_address || "",
        city: cloneFrom.pickup_city || "",
        state: cloneFrom.pickup_state || "",
        pincode: cloneFrom.pickup_pincode || "",
      },
      orderDetails: {
        ...prev.orderDetails,
        clientOrderId,
        invoiceValue: cloneFrom.payment_details?.total_amount?.toString() || "",
        invoiceNumber: cloneFrom.invoice_number || "",
        codAmount: paymentMode === "COD" ? (cloneFrom.payment_details?.total_amount?.toString() || "") : undefined,
        itemDetails,
      },
      packageDetails: {
        weight: cloneFrom.dead_weight_in_grams?.toString() || "",
        length: packageDims.length ? String(packageDims.length) : "",
        breadth: packageDims.breadth ? String(packageDims.breadth) : "",
        height: packageDims.height ? String(packageDims.height) : "",
      },
      paymentMode,
    }));

    setUnits({ weight: "gm", length: dimensionUnit, breadth: dimensionUnit, height: dimensionUnit });
    // A loaded clone starts where its data stands (Steps only) — see landingRef below.
    setPendingDraftLanding(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Start from a delivered shipment: the AWB lookup (scoped to this merchant) fills the customer
  // (the order's drop), the return location (the order's pickup), the items and the package — the
  // same mapping as before. New: the order is assessed first, and only a delivered forward
  // shipment fills the form; anything else shows why, and the form stays open for a manual return.
  const searchOrder = async (awbArg) => {
    const awb = String(typeof awbArg === "string" ? awbArg : awbNumber || "").trim().toUpperCase();
    if (!awb) { toast.error("Enter the AWB of the shipment being returned."); return; }
    setAwbLookup({ status: "searching", awb, order: null, eligibility: null });
    const authToken = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");
    try {
      const apiSearchOrder = API.searchOrders();

      const resp = await axios.post(
        apiSearchOrder,
        { merchant_id: merchantId, awb_number: awb || null, limit: 1, skip: 0, sort_field: "shipment_order_date_time_utc", sort_direction: "DESC" },
        { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
      );

      const order = resp.data?.orders?.[0];
      if (!order) { setAwbLookup({ status: "notfound", awb, order: null, eligibility: null }); return; }
      const eligibility = assessReturnEligibility(order);
      setAwbLookup({ status: "found", awb, order, eligibility });
      if (!eligibility.ok) return; // the stub says why; nothing is filled in

      // Map item details
      const item = order.shipment_packages?.[0]?.items?.[0];
      const itemDetails = item ? [{
        name: item.item_general_details?.item_name || "",
        skuId: item.item_general_details?.sku || "",
        quantity: item.quantity || 1,
        unitPrice: item.item_payment_details?.unit_price || "",
        hsnCode: item.item_general_details?.hsn_code || "",
      }] : [{ name: "", skuId: "", quantity: 1, unitPrice: "", hsnCode: "" }];

      // Build pickup (customer) and drop (return location) from the order
      const pickupData = {
        name: order.drop_name || "", phoneNumber: order.drop_poc_contact_number || "",
        email: "", address: order.drop_address || "", city: order.drop_city || "",
        state: order.drop_state || "", pincode: order.drop_pincode || ""
      };
      const dropData = {
        pickup_poc: order.pickup_name || "", pickup_poc_contact_number: order.pickup_poc_contact_number || "",
        pickup_poc_email: order.pickup_poc_email || "", pickup_address: order.pickup_address || "",
        pickup_city: order.pickup_city || "", pickup_state: order.pickup_state || "",
        pickup_postal_code: order.pickup_pincode || "",
        pickup_organisation: order.pickup_organisation || "",
      };

      const packageDims = order.shipment_package_dimensions || {};
      const packageDetails = {
        weight: order.dead_weight_in_grams?.toString() || "",
        length: packageDims.length ? String(packageDims.length) : "",
        breadth: packageDims.breadth ? String(packageDims.breadth) : "",
        height: packageDims.height ? String(packageDims.height) : "",
      };

      const dimensionUnit = packageDims.unit || 'cm';
      const newUnits = { weight: 'gm', length: dimensionUnit, breadth: dimensionUnit, height: dimensionUnit };

      // Populate formData and drop state; fill Client Order ID from order_reference_number + _REVERSE
      const clientOrderIdFromSearch = (order.order_reference_number || "") + "_REVERSE";
      setFormData(prev => ({
        ...prev,
        pickupDetails: pickupData,
        orderDetails: { ...prev.orderDetails,
          clientOrderId: clientOrderIdFromSearch,
          invoiceValue: order.payment_details?.total_amount?.toString() || "",
          invoiceNumber: order.invoice_number || "",
          codAmount: undefined,
          itemDetails
        },
        paymentMode: "Prepaid",
        packageDetails
      }));

      setUnits(newUnits);

      setDropDetails([dropData]);
      setSelectedDrop(dropData);
      setPincodeLookupFailed(false);

      // The suggested reference is prechecked by the order-reference effect like a typed one;
      // if it is taken, the Order & items section says so and blocks until it is changed there.
    } catch (err) {
      setAwbLookup({ status: "error", awb, order: null, eligibility: null });
    }
  };

  // Arriving from a shipment page ("Book a Return"): the AWB rides in the query string and the
  // lookup runs on its own, so the merchant lands on a filled form rather than an empty field.
  const bootAwb = new URLSearchParams(location?.search || "").get("awb") || "";
  useEffect(() => {
    if (!bootAwb || cloneMode) return;
    const awb = bootAwb.trim().toUpperCase();
    setAwbNumber(awb);
    searchOrder(awb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootAwb]);

  // ---Gen-ID helper ---
  const handleGenerateOrderId = () => {
    setFormData((f) => ({ ...f, orderDetails: { ...f.orderDetails, clientOrderId: uuidv4() } }));
  };

  // Helper: decode JWT and extract username (for the SKU generator prefix)
  const getUsernameFromToken = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return '';
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.username || '';
    } catch (e) {
      return '';
    }
  };
  const generateRandomSku = () => {
    const username = getUsernameFromToken();
    const prefix = username.slice(0, 3).toUpperCase();
    const randomSix = Math.floor(100000 + Math.random() * 900000);
    return `${prefix}-${randomSix}`;
  };

  // --- fetchSKUDetails populates name/price/quantity + package dims (on the SKU field's fetch action) ---
  const fetchSKUDetails = async (index, sku) => {
    if (!sku) return;
    try {
      const authToken = localStorage.getItem('authToken');
      const merchantId = localStorage.getItem('merchantId');
      const apiGetSkuDetails = API.getProductSku(merchantId, sku);
      const response = await axios.get(apiGetSkuDetails, {
        headers: { Authorization: authToken },
        validateStatus: () => true,
      });
      const data = typeof response.data === 'object' && response.data !== null ? response.data : null;
      if (!data || Object.keys(data).length === 0) return; // ignore blank/bad responses

      setFormData((prev) => {
        const items = [...prev.orderDetails.itemDetails];
        items[index] = {
          ...items[index],
          name: data.product_name || items[index].name,
          unitPrice: data.item_pricing_value?.unit_price || items[index].unitPrice,
          quantity: "1",
          skuId: items[index].skuId,
        };
        const newPackageDetails = { ...prev.packageDetails };
        if (data.product_weight?.product_weight != null) newPackageDetails.weight = data.product_weight.product_weight;
        if (data.product_dimensions?.product_length != null) newPackageDetails.length = data.product_dimensions.product_length;
        if (data.product_dimensions?.product_width != null) newPackageDetails.breadth = data.product_dimensions.product_width;
        if (data.product_dimensions?.product_height != null) newPackageDetails.height = data.product_dimensions.product_height;
        return { ...prev, orderDetails: { ...prev.orderDetails, itemDetails: items }, packageDetails: newPackageDetails };
      });
      setUnits((prev) => ({
        weight: data.product_weight?.unit || prev.weight,
        length: data.product_dimensions?.unit || prev.length,
        breadth: data.product_dimensions?.unit || prev.breadth,
        height: data.product_dimensions?.unit || prev.height,
      }));
      toast.success("Product details filled in from your catalogue.");
    } catch (err) {
      toast.error('Unable to fetch product details');
    }
  };

  // Conversion functions for payload only
  const convertLength = (value, unit) => {
    const num = parseFloat(value) || 0;
    return unit === 'inch' ? num * 2.54 : num;
  };
  const convertWeight = (value, unit) => {
    const num = parseFloat(value) || 0;
    return unit === 'kg' ? num * 1000 : num;
  };

  const handlePackageValueChange = (field, value) => {
    setFormData(prev => ({ ...prev, packageDetails: { ...prev.packageDetails, [field]: value } }));
  };
  const handlePackageUnitChange = (field, unit) => {
    setUnits(prev => ({ ...prev, [field]: unit }));
  };

  const handlePincodeChange = async (e) => {
    const pin = e.target.value.trim();
    setFormData(prev => ({ ...prev, pickupDetails: { ...prev.pickupDetails, pincode: pin, city: "", state: "" } }));
    setPincodeLookupFailed(false);

    if (!/^\d{6}$/.test(pin)) return;

    setLoadingPincode(true);
    const db = await loadPincodeDb();
    const [entry] = db ? db.search(pin) : [];
    if (entry) {
      setFormData(prev => ({ ...prev, pickupDetails: { ...prev.pickupDetails, city: entry.city, state: entry.state } }));
      setLoadingPincode(false);
      return;
    }
    try {
      const response = await axios.get(`https://api.postalpincode.in/pincode/${pin}`);
      const data = response.data?.[0];
      if (data?.Status === 'Success' && data.PostOffice?.length) {
        const postOffice = data.PostOffice[0];
        setFormData(prev => ({ ...prev, pickupDetails: { ...prev.pickupDetails, city: postOffice.District, state: postOffice.State } }));
      } else {
        throw new Error('No Postal data');
      }
    } catch (err) {
      setPincodeLookupFailed(true);
    } finally {
      setLoadingPincode(false);
    }
  };

  useEffect(() => {
    if (step === 1) {
      fetchAndStoreDropDetails();
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadPincodeDb(); }, []);

  // How this merchant's shipment charge settles (SQL wallet OR Mongo onboarding), for the ticket's note.
  // Fails open — the redux wallet type is the fallback.
  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) return undefined;
    const merchantId = localStorage.getItem("merchantId");
    let cancelled = false;
    axios
      .get(API.merchantSettlementType(), { headers: { Authorization: authToken, "x-merchant-id": merchantId }, validateStatus: () => true })
      .then((res) => {
        if (!cancelled && res && res.status >= 200 && res.status < 300) {
          setResolvedSettlement(res.data?.settlement_type ?? null);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // After a failed Continue, put the cursor in the first field that needs attention.
  useEffect(() => {
    if (!Object.keys(errors).length) return;
    document.querySelector('[aria-invalid="true"]')?.focus();
  }, [errors]);

  // On a step change after first paint: bring the card back into view if the merchant had
  // scrolled, and put the cursor in the step's first field. Nothing moves on initial load.
  const flowRef = useRef(null);
  const settledRef = useRef(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const el = flowRef.current;
      if (settledRef.current && el) {
        const top = el.getBoundingClientRect().top;
        if (top < 0 || top > 200) el.scrollIntoView({ block: "start", behavior: "smooth" });
      }
      const first = el?.querySelector('input:not([type="radio"]):not([type="search"]):not([name="awbNumber"]):not([disabled]), textarea')
        || el?.querySelector('input[type="radio"]:checked, [role="switch"]');
      first?.focus({ preventScroll: true });
    });
    settledRef.current = true;
    return () => cancelAnimationFrame(id);
  }, [step]);

  const fetchAndStoreDropDetails = async (skipAutoSelect = false) => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      return [];
    }

    const apiGetPickupAddress = API.getPickupDetails(localStorage.getItem("parentClientId"));

    try {
      const response = await axios.get(apiGetPickupAddress, { headers: { Authorization: authToken } });

      if (response.data && response.data.length > 0) {
        localStorage.setItem("dropLocations", JSON.stringify(response.data));
        const list = reconcileFetchedDrop(response.data);
        setDropDetails(list);
        // Auto-select only when nothing is chosen yet — a reload never overrides the merchant's pick.
        if (!skipAutoSelect) setSelectedDrop((prev) => prev || list[0]);
        return response.data;
      } else {
        setDropDetails([]);
        return [];
      }
    } catch (error) {
      setDropDetails([]);
      return [];
    }
  };

  useEffect(() => {
    if (step === 2) {
      const tryMatchCloneDrop = (addresses) => {
        // For reverse orders, match the drop (return warehouse) address from cloneFrom
        if (!cloneFrom) return false;
        // In reverse order drafts, the "drop" fields represent the return warehouse
        const clonedPostal = (cloneFrom.drop_pincode || cloneFrom.drop_postal_code || cloneFrom.pickup_pincode || cloneFrom.pickup_postal_code || "").trim();
        const clonedPhone = (cloneFrom.drop_poc_contact_number || cloneFrom.pickup_poc_contact_number || "").trim();
        const clonedAddress = (cloneFrom.drop_address || cloneFrom.pickup_address || "").trim().toLowerCase();

        if (!clonedPostal && !clonedPhone && !clonedAddress) return false;

        const match = addresses.find((addr) => {
          const addrPostal = (addr.pickup_postal_code || "").trim();
          const addrPhone = (addr.pickup_poc_contact_number || "").trim();
          const addrAddress = (addr.pickup_address || "").trim().toLowerCase();

          if (clonedPostal && addrPostal === clonedPostal) {
            if (clonedPhone && addrPhone === clonedPhone) return true;
            if (clonedAddress && addrAddress.includes(clonedAddress.slice(0, 30))) return true;
          }
          return false;
        });

        if (match) {
          setSelectedDrop(match);
          return true;
        }

        // No match - auto-open "Add New Address" modal pre-filled
        const mappedDrop = {
          pickup_address: cloneFrom.drop_address || cloneFrom.pickup_address || "",
          pickup_city: cloneFrom.drop_city || cloneFrom.pickup_city || "",
          pickup_postal_code: cloneFrom.drop_pincode || cloneFrom.drop_postal_code || cloneFrom.pickup_pincode || cloneFrom.pickup_postal_code || "",
          pickup_poc: cloneFrom.drop_name || cloneFrom.drop_poc || cloneFrom.pickup_name || cloneFrom.pickup_poc || "",
          pickup_organisation: cloneFrom.drop_organisation || cloneFrom.pickup_organisation || "",
          pickup_poc_contact_number: cloneFrom.drop_poc_contact_number || cloneFrom.pickup_poc_contact_number || "",
          pickup_poc_email: cloneFrom.drop_poc_email || cloneFrom.pickup_poc_email || "",
          pickup_state: cloneFrom.drop_state || cloneFrom.pickup_state || "",
          pickup_district: cloneFrom.drop_district || cloneFrom.pickup_district || "",
          pickup_country_code: cloneFrom.drop_country_code || cloneFrom.pickup_country_code || "IN",
          pickup_landmark: cloneFrom.drop_landmark || cloneFrom.pickup_landmark || "",
          gst_number: cloneFrom.gst_number || "",
        };
        const hasData = mappedDrop.pickup_postal_code || mappedDrop.pickup_address || mappedDrop.pickup_poc;
        if (hasData) {
          setAddrModalMode("add");
          setAddrModalLocation(mappedDrop);
          setAddrModalOpen(true);
        }
        return false;
      };

      const stored = JSON.parse(localStorage.getItem("dropLocations") || "[]");
      if (Array.isArray(stored) && stored.length > 0) {
        const list = reconcileFetchedDrop(stored);
        setDropDetails(list);
        if (!tryMatchCloneDrop(list)) {
          setSelectedDrop((prev) => prev || list[0]);
        }
      } else {
        fetchAndStoreDropDetails(true).then((fetched) => {
          if (fetched && fetched.length > 0) {
            if (!tryMatchCloneDrop(fetched)) {
              setSelectedDrop((prev) => prev || fetched[0]);
            }
          } else if (cloneFrom) {
            tryMatchCloneDrop([]);
          }
        });
      }
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Address management handlers ── */
  const openAddAddress = () => { setAddrModalMode("add"); setAddrModalLocation(null); setAddrModalOpen(true); };
  const openEditAddress = (loc) => { setAddrModalMode("edit"); setAddrModalLocation(loc); setAddrModalOpen(true); };
  const handleAddressSaved = () => {
    fetchAndStoreDropDetails().then((fetched) => {
      if (fetched && fetched.length > 0) {
        setSelectedDrop(fetched[fetched.length - 1]);
      }
    });
  };
  const openDeleteAddress = (id) => { setPickupToDelete(id); setDeleteConfirmOpen(true); };
  const confirmDeleteAddress = async () => {
    setDeletingPickup(true);
    await deletePickupAddress(pickupToDelete, () => {
      fetchAndStoreDropDetails();
      if (selectedDrop?.pickup_address_id === pickupToDelete) setSelectedDrop(null);
    });
    setDeletingPickup(false);
    setDeleteConfirmOpen(false);
    setPickupToDelete(null);
  };
  // Refresh the saved locations, spinning the glyph for at least ~a full turn so the click is
  // visibly acknowledged even when the list comes back unchanged.
  const refreshDropLocations = async () => {
    if (refreshingDrops) return;
    setRefreshingDrops(true);
    const t0 = Date.now();
    try { await Promise.resolve(fetchAndStoreDropDetails()); } catch { /* the fetch reports its own errors */ }
    window.setTimeout(() => setRefreshingDrops(false), Math.max(0, 900 - (Date.now() - t0)));
  };

  const steps = [
    { key: "pickupDetails", label: "Customer" },
    { key: "dropDetails", label: "Return to" },
    { key: "orderDetails", label: "Order & items" },
    { key: "packageDetails", label: "Package" },
    { key: "courierDetails", label: "Courier" },
    { key: "summary", label: "Booked" },
  ];

  // The rules for one section, as an errors object. validateStep (the stepper's Continue) and
  // validateAll (Book in the stacked layout) both run exactly these.
  const validateSection = (currentSection) => {
    const newErrors = {};
    const addErr = (k, msg) => { newErrors[k] = msg; };

    if (currentSection === "dropDetails" && !selectedDrop) {
      addErr("drop", "Choose a return location to continue");
    }

    if (currentSection === "pickupDetails") {
      const cd = formData.pickupDetails || {};
      if (!String(cd.name || "").trim()) addErr("name", "Name is required");
      if (!/^[0-9]{10}$/.test(String(cd.phoneNumber || ""))) addErr("phoneNumber", "Phone Number must be 10 digits");
      const addr = String(cd.address || "").trim();
      if (!addr) addErr("address", "Address is required");
      else if (addr.length < 10) addErr("address", "Address looks too short");
      if (!String(cd.city || "").trim()) addErr("city", "City is required");
      if (!String(cd.state || "").trim()) addErr("state", "State is required");
      if (!/^[0-9]{6}$/.test(String(cd.pincode || ""))) addErr("pincode", "Pincode must be 6 digits");
      const email = String(cd.email || "").trim();
      if (email && !EMAIL_RE.test(email)) addErr("email", "Enter a valid email address");
    }

    if (currentSection === "orderDetails") {
      const od = formData.orderDetails || {};
      if (!String(od.clientOrderId || "").trim()) addErr("clientOrderId", "Client OrderID is required");
      else if (refBlockedFor(String(od.clientOrderId || "").trim())) addErr("clientOrderId", "A return already exists for this reference");
      const invoiceNum = parseFloat(od.invoiceValue || 0);
      if (!(Number.isFinite(invoiceNum) && invoiceNum > 0)) addErr("invoiceValue", "Invoice Value must be greater than 0");
      (od.itemDetails || []).forEach((item, index) => {
        if (!String(item.name || "").trim()) addErr(`name-${index}`, "Name is required");
        if (!cloneMode && !String(item.skuId || "").trim()) addErr(`skuId-${index}`, "SKU ID is required");
        if (!String(item.quantity ?? "").trim() || Number(item.quantity) <= 0 || !Number.isInteger(Number(item.quantity))) addErr(`quantity-${index}`, "Quantity is required and must be > 0");
        if (!String(item.unitPrice ?? "").trim() || Number(item.unitPrice) < 0) addErr(`unitPrice-${index}`, "Unit Price is required");
      });
    }

    if (currentSection === "packageDetails") {
      const pd = formData.packageDetails || {};
      const weightVal = String(pd.weight ?? "").trim();
      if (!weightVal) addErr("weight", "Weight is required");
      else if (isNaN(Number(weightVal)) || Number(weightVal) <= 0) addErr("weight", "Weight must be a valid number greater than 0");
      else if ((units.weight === "kg" ? Number(weightVal) : Number(weightVal) / 1000) > 200) addErr("weight", "Weight looks too high - check the unit");
      [["length", "Length"], ["breadth", "Breadth"], ["height", "Height"]].forEach(([k, label]) => {
        const v = String(pd[k] ?? "").trim();
        if (!v) addErr(k, `${label} is required`);
        else if (isNaN(Number(v)) || Number(v) <= 0) addErr(k, `${label} must be a valid number greater than 0`);
        else if ((units[k] === "inch" ? Number(v) * 2.54 : Number(v)) > 500) addErr(k, `${label} looks too high - check the unit`);
      });
    }

    return newErrors;
  };
  const validateStep = () => {
    const newErrors = validateSection(steps[step - 1].key);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const validateAll = () => {
    const all = {};
    let firstBad = null;
    for (let n = 1; n <= 5; n++) {
      const errs = validateSection(steps[n - 1].key);
      if (Object.keys(errs).length && firstBad == null) firstBad = n;
      Object.assign(all, errs);
    }
    setErrors(all);
    return { ok: Object.keys(all).length === 0, firstBad };
  };

  const handleNext = () => {
    if (deskLocked) return; // refused shipment, or the quick-fill's reference still taken
    if (validateStep()) {
      if (step === 5) {
        handleSubmit();
      } else {
        const nextStep = step === 4 ? 5 : step + 1;
        setStep(nextStep);
        setHighestStepReached(prev => Math.max(prev, nextStep));
        if (step === 4) {
          lastCourierKeyRef.current = courierInputsKey;
          prefetchCouriersForStep5();
        }
      }
    }
  };

  /* "↵ Enter to continue" everywhere the native submit doesn't reach (no-input steps, the address
     textarea, blank focus). Inputs on steps 1-4 keep the native implicit submission; buttons, links
     and open dialogs keep their own Enter. The AWB field has its own Enter (fetch). */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter" || e.isComposing || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      if (loading || savingDraft || searchingAwb) return;
      if (addrModalOpen || deleteConfirmOpen || showCustomerModal || draftSuccessModal.open) return;
      const tag = document.activeElement?.tagName;
      if (tag === "BUTTON" || tag === "A") return;
      if (document.activeElement?.name === "awbNumber") return;
      if (stacked) return; // stacked: Book is a deliberate click, never Enter
      if (step <= 4 && (tag === "INPUT" || tag === "SELECT")) return;
      e.preventDefault();
      handleNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const handleBack = () => {
    setStep(prev => (prev > 1 ? prev - 1 : prev));
  };

  /* ── Stacked layout ── */
  const scrollToSection = (n) => {
    const el = document.querySelector(`[data-section="${n}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const handleBookStacked = () => {
    if (deskLocked) return;
    const { ok, firstBad } = validateAll();
    if (!ok) { if (firstBad) scrollToSection(firstBad); return; }
    handleSubmit();
  };
  const chooseLayout = (mode) => {
    if (mode === layoutMode) return;
    setLayoutMode(mode);
    writeLayoutPreference(mode);
    if (mode === "vertical") { setStep(5); setHighestStepReached(5); return; }
    const firstIncomplete = steps.slice(0, 5).findIndex((s) => Object.keys(validateSection(s.key)).length > 0);
    const landing = firstIncomplete === -1 ? 5 : firstIncomplete + 1;
    setStep(landing);
    setHighestStepReached(landing);
    setErrors({});
  };
  useEffect(() => {
    if (stacked && step < 5) { setStep(5); setHighestStepReached(5); }
  }, [stacked, step]);
  // Stacked has no Continue on the Package step, so the courier rates fetch on their own once
  // customer, return location and package are valid — and again when any of those inputs change.
  const courierInputsKey = JSON.stringify([
    formData.pickupDetails.pincode, selectedDrop?.pickup_address_id, selectedDrop?.pickup_postal_code,
    formData.orderDetails.invoiceValue, formData.packageDetails, units,
  ]);
  const courierInputsValid = stacked
    && Object.keys(validateSection("pickupDetails")).length === 0
    && Object.keys(validateSection("dropDetails")).length === 0
    && Object.keys(validateSection("packageDetails")).length === 0;
  const sectionValid = stacked ? steps.slice(0, 5).map((s) => Object.keys(validateSection(s.key)).length === 0) : [];
  const unlockedUpTo = stacked ? (sectionValid.findIndex((ok) => !ok) === -1 ? 5 : sectionValid.findIndex((ok) => !ok) + 1) : 5;
  const lastCourierKeyRef = useRef("");
  const prefetchRef = useRef(null);
  useEffect(() => {
    if (!stacked || !courierInputsValid || courierInputsKey === lastCourierKeyRef.current) return undefined;
    const t = window.setTimeout(() => { lastCourierKeyRef.current = courierInputsKey; prefetchRef.current(); }, 500);
    return () => window.clearTimeout(t);
  }, [stacked, courierInputsValid, courierInputsKey]);

  const handleStepClick = (targetStep) => {
    if (deskLocked) return;
    if (targetStep <= highestStepReached && targetStep !== step) {
      setStep(targetStep);
    }
  };

  const handleCourierSelection = async (useRecommendation) => {
    setFormData((prev) => ({
      ...prev,
      courierDetails: {
        ...prev.courierDetails,
        useRecommendation,
        courierPartner: useRecommendation ? "" : prev.courierDetails.courierPartner,
      },
    }));
  };

  const prefetchCouriersForStep5 = async () => {
    try {
      setLoadingCouriers(true);
      await Promise.all([
        fetchCourierPricing(),
        fetchServiceability(),
      ]);
    } finally {
      setLoadingCouriers(false);
    }
  };
  prefetchRef.current = prefetchCouriersForStep5;
  // Where a freshly hydrated clone should land (Steps only): the first section that doesn't
  // validate yet. Render-fresh values are read through a ref so the effect depends on one thing.
  const landingRef = useRef(null);
  landingRef.current = () => {
    if (stacked) return;
    const firstIncomplete = steps.slice(0, 5).findIndex((s) => Object.keys(validateSection(s.key)).length > 0);
    const landing = firstIncomplete === -1 ? 5 : firstIncomplete + 1;
    setStep(landing);
    setHighestStepReached(landing);
    if (landing === 5) { lastCourierKeyRef.current = courierInputsKey; prefetchCouriersForStep5(); }
  };
  useEffect(() => {
    if (!pendingDraftLanding) return;
    setPendingDraftLanding(false);
    landingRef.current();
  }, [pendingDraftLanding]);

  const handlePhoneNumberChange = async (e) => {
    const phoneNumber = e.target.value.trim()
    setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, phoneNumber } }))

    if (/^\d{10}$/.test(phoneNumber)) {
      try {
        const authToken = localStorage.getItem('authToken')
        const merchantId = localStorage.getItem('merchantId')
        const apiAddressLookupFromMobile = API.addressLookupFromMobile();
        const response = await axios.request({
          method: 'POST',
          url: apiAddressLookupFromMobile,
          headers: { Authorization: authToken, 'Content-Type': 'application/json' },
          data: { phone_number: phoneNumber, merchant_id: merchantId },
        })

        if (Array.isArray(response.data) && response.data.length > 0) {
          setCustomerLookupResults(response.data)
          setShowCustomerModal(true)
        }
      } catch (err) {
        // lookup is best-effort
      }
    }
  }

  const selectCustomer = (cust) => {
    setFormData((prev) => ({
      ...prev,
      pickupDetails: {
        ...prev.pickupDetails,
        name: cust.drop_poc || '',
        phoneNumber: cust.drop_poc_contact_number || prev.pickupDetails.phoneNumber,
        email: cust.drop_poc_email || prev.pickupDetails.email,
        address: cust.drop_address || '',
        city: cust.drop_city || '',
        state: cust.drop_state || '',
        pincode: cust.drop_postal_code || '',
      },
    }))
    setShowCustomerModal(false)
  }

  const fetchCourierPricing = async () => {
    setLoadingCouriers(true);
    setCourierOptions([]);
    try {
      const allocatedCarriersRaw = JSON.parse(localStorage.getItem("allocatedCarriers") || "[]");
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      const apiFetchCourierPricing = API.merchantPricingBatch();

      if (!authToken) {
        toast.error("Missing authentication.");
        setLoadingCouriers(false);
        return;
      }

      // Reverse order: send only REVERSE carrier partners in the request payload
      const reverseMatcher = /(_B2CREVERSE_|REVERSE_)/i; // matches “…_B2CREVERSE_…” or “…REVERSE_…”
      const reverseCarriers = allocatedCarriersRaw
        .map((c) => (typeof c === "string" ? { carrier_partner_id: c } : c))
        .filter(
          (c) =>
            c &&
            typeof c.carrier_partner_id === "string" &&
            reverseMatcher.test(c.carrier_partner_id)
        );

      if (!reverseCarriers.length) {
        toast.info("No reverse-enabled carriers allocated.");
        setLoadingCouriers(false);
        return;
      }

      const requestPayload = reverseCarriers.map((carrier) => ({
        correlation_id: uuidv4(),
        carrier_partner_id: carrier.carrier_partner_id,
        shipment_type: "RVP",
        payment_mode: "PREPAID",
        invoice_value: parseFloat(formData.orderDetails.invoiceValue),
        rvp_with_quality_check: false,
        weight_in_grams: convertWeight(formData.packageDetails.weight, units.weight),
        length_in_cm: convertLength(formData.packageDetails.length, units.length),
        breadth_in_cm: convertLength(formData.packageDetails.breadth, units.breadth),
        height_in_cm: convertLength(formData.packageDetails.height, units.height),
        drop_pincode: selectedDrop?.pickup_postal_code,
        pickup_pincode: formData.pickupDetails.pincode,
      }));

      const response = await axios.post(
        apiFetchCourierPricing,
        requestPayload,
        { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
      );

      // Deduplicate by carrier_partner_public_name: keep first occurrence only
      const seenNames = new Set();
      const formattedOptions = (response.data || [])
        .filter((rate) => {
          const name = rate.carrier_partner_public_name;
          if (seenNames.has(name)) return false;
          seenNames.add(name);
          return true;
        })
        .map((rate) => ({
          carrier_partner_public_name: rate.carrier_partner_public_name,
          total_amount_charged_after_tax: Number(Number(rate.total_amount_charged_after_tax).toFixed(2)),
          max_tat_in_days: rate.max_tat_in_days,
        }));

      setCourierOptions(formattedOptions);
      // A manual pick belongs to the rate list it was made from; once the list is replaced, drop it.
      setFormData((prev) => {
        const picked = prev.courierDetails.courierPartner;
        if (!picked || prev.courierDetails.useRecommendation) return prev;
        toast.info("Return details changed — pick a courier again from the updated rates.");
        return { ...prev, courierDetails: { ...prev.courierDetails, courierPartner: "" } };
      });
    } catch (error) {
      toast.error("Failed to fetch courier rates.");
    } finally {
      setLoadingCouriers(false);
    }
  };

  const fetchServiceability = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        toast.error("Missing auth token.");
        return;
      }
      const apiFetchServiceabilty = API.checkServiceability();
      const payload = {
        drop_pincode: selectedDrop?.pickup_postal_code,
        pickup_pincode: formData.pickupDetails.pincode,
      };
      const response = await axios.post(apiFetchServiceabilty, payload, {
        headers: { Authorization: authToken, "Content-Type": "application/json" },
      });
      if (
        response.data &&
        response.data.merchant_domestic_serviceabilities &&
        response.data.merchant_domestic_serviceabilities.length > 0
      ) {
        const serviceData = response.data.merchant_domestic_serviceabilities
          .filter((p) => /REVERSE/i.test(p.carrier_partner_id || ""))
          .map((partner) => ({
            id: partner.carrier_partner_id,
            name: (partner.carrier_partner_identifier_string || "N/A").trim(),
            prepaid: partner.prepaid_serviceability_details?.is_serviceable ?? false,
            cod: partner.cod_serviceability_details?.is_serviceable ?? false,
          }));
        setCourierServiceability(serviceData);
      } else {
        toast.error("No serviceability data available.");
      }
    } catch (error) {
      toast.error("Failed to fetch serviceability details.");
    }
  };

  const handleItemChange = (index, key, value) => {
    const updatedItems = [...formData.orderDetails.itemDetails];
    updatedItems[index] = { ...updatedItems[index], [key]: value };
    setFormData((prev) => ({ ...prev, orderDetails: { ...prev.orderDetails, itemDetails: updatedItems } }));
  };

  const removeItemDetail = (index) => {
    if (formData.orderDetails.itemDetails.length > 1) {
      const filteredItems = formData.orderDetails.itemDetails.filter((_, idx) => idx !== index);
      setFormData({ ...formData, orderDetails: { ...formData.orderDetails, itemDetails: filteredItems } });
    } else {
      // The only item: clear it back to a blank row rather than leaving zero items.
      setFormData({ ...formData, orderDetails: { ...formData.orderDetails, itemDetails: [{ name: "", quantity: 1, unitPrice: "", skuId: "", hsnCode: "" }] } });
      setExpandedHSNItems({});
    }
  };

  const addItemDetail = () => {
    const newItem = { name: "", quantity: 1, unitPrice: "", skuId: "", hsnCode: "" };
    setFormData({ ...formData, orderDetails: { ...formData.orderDetails, itemDetails: [...formData.orderDetails.itemDetails, newItem] } });
  };

  const buildApiPayload = (trimmedForm) => {
    return {
      shipment_order_type: "RVP",
      process_shipment_order_in_async: false,
      pickup_details: {
        pickup_address: trimmedForm.pickupDetails.address,
        pickup_city: trimmedForm.pickupDetails.city,
        pickup_state: trimmedForm.pickupDetails.state,
        pickup_postal_code: trimmedForm.pickupDetails.pincode,
        pickup_poc: trimmedForm.pickupDetails.name,
        pickup_country_code: "IN",
        pickup_poc_contact_number: trimmedForm.pickupDetails.phoneNumber,
        pickup_poc_email: trimmedForm.pickupDetails.email || "abc@gmail.com",
      },
      drop_details: {
        drop_poc: selectedDrop.pickup_poc,
        drop_address: selectedDrop.pickup_address,
        drop_city: selectedDrop.pickup_city,
        drop_country_code: "IN",
        drop_poc_contact_number: selectedDrop.pickup_poc_contact_number,
        drop_poc_email: selectedDrop.pickup_poc_email || "abc@gmail.com",
        drop_state: selectedDrop.pickup_state,
        drop_postal_code: selectedDrop.pickup_postal_code,
      },
      reverse_pickup_details: {
        reverse_pickup_reason: "reverse order",
      },
      shipment_details: {
        ...(!trimmedForm.courierDetails.useRecommendation && trimmedForm.courierDetails.courierPartner && {
          shipment_carrier_details: {
            carrier_partner_public_name: trimmedForm.courierDetails.courierPartner,
          },
        }),
        order_details: {
          order_type: "PREPAID",
          order_reference_number: trimmedForm.orderDetails.clientOrderId,
          order_invoice_details: {
            invoice_value: parseFloat(trimmedForm.orderDetails.invoiceValue),
            invoice_number: trimmedForm.orderDetails.invoiceNumber || null,
            invoice_currency: "INR"
          },
        },
        shipment_packages: [
          {
            items: trimmedForm.orderDetails.itemDetails.map(item => {
              const itemGeneralDetails = { item_name: item.name, sku: item.skuId };
              if (item.hsnCode && String(item.hsnCode).trim()) itemGeneralDetails.hsn_code = String(item.hsnCode).trim();
              return {
                item_general_details: itemGeneralDetails,
                quantity: item.quantity,
                item_payment_details: { unit_price: item.unitPrice }
              };
            }),
            dimensions: {
              length: convertLength(trimmedForm.packageDetails.length, units.length),
              breadth: convertLength(trimmedForm.packageDetails.breadth, units.breadth),
              height: convertLength(trimmedForm.packageDetails.height, units.height),
              unit: 'cm'
            },
            weight: {
              value: convertWeight(trimmedForm.packageDetails.weight, units.weight),
              unit: "gm"
            }
          }
        ]
      }
    };
  };

  const handleSaveAsDraft = async () => {
    const trimmedForm = deepTrimStrings(formData);
    setFormData(trimmedForm);

    const orderData = buildApiPayload(trimmedForm);

    setSavingDraft(true);
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        navigate("/login");
        return;
      }

      const merchantId = localStorage.getItem("merchantId");
      const draftPayload = {
        order_data: orderData,
        order_source: "UI_PANEL",
        ...(trimmedForm.orderDetails.clientOrderId && {
          title: `Reverse Order ${trimmedForm.orderDetails.clientOrderId}`
        }),
      };

      const apiUrl = draftOrderId
        ? API.updateDraftOrder(draftOrderId)
        : API.createDraftOrder();

      const response = draftOrderId
        ? await axios.put(apiUrl, draftPayload, { headers: { 'Authorization': `${authToken}`, 'Content-Type': 'application/json', 'merchantId': merchantId } })
        : await axios.post(apiUrl, draftPayload, { headers: { 'Authorization': `${authToken}`, 'Content-Type': 'application/json', 'merchantId': merchantId } });

      if (response.data.metadata?.success !== false && response.data.draft_order_id) {
        const newDraftId = response.data.draft_order_id;
        if (!draftOrderId) setDraftOrderId(newDraftId);
        setDraftSuccessModal({ open: true, draftId: newDraftId, isUpdate: Boolean(draftOrderId) });
      } else if (response.data.metadata?.success) {
        const newDraftId = draftOrderId || response.data.draft_order_id;
        if (!draftOrderId && newDraftId) setDraftOrderId(newDraftId);
        setDraftSuccessModal({ open: true, draftId: newDraftId, isUpdate: Boolean(draftOrderId) });
      } else {
        toast.error(response.data.metadata?.message || "Failed to save draft.");
      }
    } catch (error) {
      const errMsg = error?.response?.data?.metadata?.message || "Error saving draft. Please try again.";
      toast.error(errMsg);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    // Onboarding gate: the form stays usable; placing the order requires an activated account.
    if (!requireBusinessOps('create your reverse order')) return;

    // Backstop for the order-reference precheck (the field-level gate already blocks Continue).
    const submitRef = String(formData.orderDetails.clientOrderId || "").trim();
    if (refBlockedFor(submitRef)) {
      toast.error("A return already exists for this reference. Use a different reference.");
      return;
    }

    const trimmedForm = deepTrimStrings(formData);
    setFormData(trimmedForm);

    const apiPayload = buildApiPayload(trimmedForm);

    setLoading(true);
    setResponseData(null);

    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        navigate("/login");
        return;
      }

      const merchantId = localStorage.getItem("merchantId");
      const apiUrl = API.createOrder();

      const response = await axios.post(apiUrl, apiPayload, {
        headers: { 'Authorization': `${authToken}`, 'Content-Type': 'application/json', 'x-client-id': merchantId }
      });

      const outcome = interpretOrderCreationResponse(response.data);
      if (outcome.kind === "success") {
        setResponseData(outcome.result);
        setBookedAt(new Date());
        dispatch(fetchWalletBalance());
        new BroadcastChannel('wallet').postMessage('refresh-balance');
        setStep(6);
      } else {
        setOrderIssue(outcome.issue);
      }
    } catch (error) {
      setOrderIssue(interpretOrderCreationError(error));
    } finally {
      setLoading(false);
    }
  };

  // "Review order" lands on the section the issue points at: carrier problems on the courier
  // section, a reference clash on Order & items, pincode / route problems and everything else on
  // the customer section. "Stay on this order" only closes the modal.
  const reviewAfterIssue = (issue) => {
    const label = String(issue?.actionLabel || "").toLowerCase();
    const target = /carrier/.test(label) ? 5 : /reference/.test(label) ? 3 : 1;
    if (stacked) { scrollToSection(target); return; }
    setStep(target);
    setHighestStepReached((prev) => Math.max(prev, target));
    window.setTimeout(() => flowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };
  const handleOrderIssueAction = (action, issue) => {
    setOrderIssue(null);
    if (action === "recharge") navigate("/wallet?recharge=1");
    else if (action === "wallet") navigate("/wallet");
    else if (action === "orders") navigate("/orders");
    else if (action === "login") navigate("/login");
    else if (action === "retry") handleSubmit();
    else if (action === "review") reviewAfterIssue(issue);
  };

  /* ─────────────────────────────────────────────────────────────────────────
     Render-only derivations. Everything below reads state; nothing writes it.
  ───────────────────────────────────────────────────────────────────────── */
  const cd = formData.pickupDetails; // the customer
  const od = formData.orderDetails;
  const pd = formData.packageDetails;
  const invoiceValueNum = parseFloat(od.invoiceValue || 0);
  const nonEmpty = (v) => String(v ?? "").trim() !== "";

  // Order-reference block: an existing return for the typed reference, in a state OMS would
  // answer with the old order instead of creating a new one. A failed earlier attempt only informs.
  const orderRefForBlock = String(od.clientOrderId || "").trim();
  const orderRefBlocked = refBlockedFor(orderRefForBlock);
  const orderRefFailedBefore = !cloneMode && orderRefForBlock !== "" && orderRefCheck.ref === orderRefForBlock && orderRefCheck.outcome === "FAILED";
  const orderRefBlockInfo = (() => {
    if (!orderRefBlocked) return null;
    const href = `/shipment-details/${encodeURIComponent(orderRefForBlock)}`;
    switch (orderRefCheck.outcome) {
      case "PLACED": return { msg: `A return with this reference is already booked${orderRefCheck.awb ? ` (AWB ${orderRefCheck.awb})` : ""}. Even if it was cancelled, booking again would return that same order instead of creating a new one — use a different reference.`, linkLabel: "View that return", href };
      case "PROCESSING": return { msg: "A return for this reference is still being processed — check back in a moment.", linkLabel: "View that return", href };
      case "CONFIRMATION_PENDING": return { msg: "A return for this reference is awaiting the customer's confirmation.", linkLabel: "View that return", href };
      default: return { msg: "A return already exists for this reference. Use a different reference for a new one.", linkLabel: "View that return", href };
    }
  })();
  // One lock descriptor for every surface that reacts to it (wrapper, rail, footer, buttons,
  // stacked chips). Only a refused shipment locks the desk; a taken reference is handled where the
  // merchant enters it, in Order & items.
  const deskLock = lookupRefused
    ? { reason: "refused", chip: "Clear the AWB above to continue", footer: "This shipment can't be returned — clear the AWB to continue.", title: "Clear the AWB to continue" }
    : null;
  const deskLocked = Boolean(deskLock);
  // A blocked reference stops the desk at the Order step (and Book in the stacked layout): the
  // buttons show it instead of refusing on click.
  const refStopsHere = orderRefBlocked && (stacked || step === 3);

  const stillBad = (key) => {
    const m = /^(name|skuId|quantity|unitPrice)-(\d+)$/.exec(key);
    if (m) {
      const it = (od.itemDetails || [])[Number(m[2])] || {};
      if (m[1] === "name") return !nonEmpty(it.name);
      if (m[1] === "skuId") return !cloneMode && !nonEmpty(it.skuId);
      if (m[1] === "quantity") return !nonEmpty(it.quantity) || Number(it.quantity) <= 0 || !Number.isInteger(Number(it.quantity));
      return !nonEmpty(it.unitPrice) || Number(it.unitPrice) < 0;
    }
    switch (key) {
      case "phoneNumber": return !/^[0-9]{10}$/.test(String(cd.phoneNumber || ""));
      case "name": return !nonEmpty(cd.name);
      case "email": { const em = String(cd.email || "").trim(); return !!em && !EMAIL_RE.test(em); }
      case "drop": return !selectedDrop;
      case "address": return String(cd.address || "").trim().length < 10;
      case "city": return !nonEmpty(cd.city);
      case "state": return !nonEmpty(cd.state);
      case "pincode": return !/^[0-9]{6}$/.test(String(cd.pincode || ""));
      case "clientOrderId": return !nonEmpty(od.clientOrderId) || orderRefBlocked;
      case "invoiceValue": { const n = parseFloat(od.invoiceValue || 0); return !(Number.isFinite(n) && n > 0); }
      case "weight": case "length": case "breadth": case "height": {
        const v = String(pd[key] ?? "").trim();
        if (!v || isNaN(Number(v)) || Number(v) <= 0) return true;
        if (key === "weight") return (units.weight === "kg" ? Number(v) : Number(v) / 1000) > 200;
        return (units[key] === "inch" ? Number(v) * 2.54 : Number(v)) > 500;
      }
      default: return true;
    }
  };
  const msgFor = (key) => {
    const m = /^(name|skuId|quantity|unitPrice)-\d+$/.exec(key);
    return FIELD_MSG[m ? "item-" + m[1] : key] || "Required";
  };
  // clientOrderId's block message is link-rich and renders as its own band below the field;
  // suppress the generic message there to avoid a duplicate.
  const E = (key) => {
    if (key === "clientOrderId" && orderRefBlocked) return undefined;
    return (errors[key] || touched[key]) && stillBad(key) ? msgFor(key) : undefined;
  };
  const keyStep = (key) => (/^(name|skuId|quantity|unitPrice)-\d+$/.test(key) ? 3 : FIELD_STEP[key]);
  const liveErrCount = [...new Set([...Object.keys(errors), ...Object.keys(touched)])]
    .filter((k) => keyStep(k) === step && stillBad(k)).length;
  const handleFieldBlur = (e) => {
    const key = e.target && e.target.dataset ? e.target.dataset.vkey : undefined;
    if (!key) return;
    const badNumber = e.target.type === "number" && e.target.validity && e.target.validity.badInput;
    if (!badNumber && !String(e.target.value ?? "").trim()) return;
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  /* Group-level completeness for the journey spine. Render-only; validateStep still owns the gate. */
  const emailEntered = String(cd.email || "").trim();
  const contactDone = /^[0-9]{10}$/.test(String(cd.phoneNumber || "")) && nonEmpty(cd.name) &&
    (!emailEntered || EMAIL_RE.test(emailEntered));
  const addressDone = String(cd.address || "").trim().length >= 10 && /^[0-9]{6}$/.test(String(cd.pincode || "")) && nonEmpty(cd.city) && nonEmpty(cd.state);
  const paymentDone = Number.isFinite(invoiceValueNum) && invoiceValueNum > 0;
  const orderDone = nonEmpty(od.clientOrderId) && !orderRefBlocked;
  const itemsDone = od.itemDetails.length > 0 && od.itemDetails.every(it =>
    nonEmpty(it.name) && (cloneMode || nonEmpty(it.skuId)) &&
    nonEmpty(it.quantity) && Number(it.quantity) > 0 && Number.isInteger(Number(it.quantity)) &&
    nonEmpty(it.unitPrice) && Number(it.unitPrice) >= 0);
  const weightDone = ["weight", "length", "breadth", "height"].every(k => {
    const v = String(pd[k] ?? "").trim();
    if (!v || isNaN(Number(v)) || Number(v) <= 0) return false;
    if (k === "weight") return (units.weight === "kg" ? Number(v) : Number(v) / 1000) <= 200;
    return (units[k] === "inch" ? Number(v) * 2.54 : Number(v)) <= 500;
  });
  const dropDone = Boolean(selectedDrop);

  const deadKg = convertWeight(pd.weight, units.weight) / 1000;
  const volKg =
    pd.length && pd.breadth && pd.height
      ? (convertLength(pd.length, units.length) *
          convertLength(pd.breadth, units.breadth) *
          convertLength(pd.height, units.height)) / 5000
      : null;
  const billedKg = volKg != null && pd.weight ? Math.max(deadKg, volKg) : null;
  const billedBasis = billedKg == null ? null : billedKg === deadKg && deadKg >= (volKg ?? 0) ? "dead" : "vol";

  const itemsSubtotal = od.itemDetails.reduce(
    (sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0), 0);
  const invoiceMismatch =
    itemsSubtotal > 0 && parseFloat(od.invoiceValue || 0) > 0 &&
    Math.abs(itemsSubtotal - parseFloat(od.invoiceValue)) > 0.009;

  // Reverse shipments are always prepaid: a courier is serviceable when it serves the lane prepaid.
  const serviceableOf = (option) => courierServiceability
    .filter(s => s.name === (option.carrier_partner_public_name || "").trim())
    .some(s => s.prepaid);
  const sortedOptions = [...courierOptions].sort((a, b) => {
    const sa = serviceableOf(a) ? 0 : 1;
    const sb = serviceableOf(b) ? 0 : 1;
    if (sa !== sb) return sa - sb;
    return Number(a.total_amount_charged_after_tax) - Number(b.total_amount_charged_after_tax);
  });
  const serviceableOptions = sortedOptions.filter(serviceableOf);
  const unserviceableOptions = sortedOptions.filter(o => !serviceableOf(o));
  const finiteOf = (list, pick) => list.map(pick).filter(Number.isFinite);
  const cheapestPrices = finiteOf(serviceableOptions, (o) => Number(o.total_amount_charged_after_tax));
  // A lane without a turnaround time counts as the assumed figure it displays ("Usually 5 days") —
  // for the Fastest sort and the Fastest tag alike; on a tie, a real time leads an assumed one.
  const hasTat = (o) => Number.isFinite(Number(o.max_tat_in_days));
  const tatOf = (o) => (hasTat(o) ? Number(o.max_tat_in_days) : ASSUMED_TAT_DAYS);
  const fastestTats = serviceableOptions.map(tatOf);
  const cheapestPrice = cheapestPrices.length ? Math.min(...cheapestPrices) : null;
  const fastestTat = fastestTats.length ? Math.min(...fastestTats) : null;
  const chosenOption = courierOptions.find(o => o.carrier_partner_public_name === formData.courierDetails.courierPartner);
  const serviceableDisplay = courierSort === "fastest"
    ? [...serviceableOptions].sort((a, b) =>
        (tatOf(a) - tatOf(b)) ||
        (Number(hasTat(b)) - Number(hasTat(a))) ||
        (Number(a.total_amount_charged_after_tax) - Number(b.total_amount_charged_after_tax)))
    : serviceableOptions;
  const courierRoute = (cd.city && selectedDrop?.pickup_city)
    ? `${titleCaseCity(cd.city)} → ${titleCaseCity(selectedDrop.pickup_city)}` : null;
  // "SHADOWFAX REVERSE SURFACE" → "Shadowfax · Surface" for the ticket row
  const prettyCourier = (name) => { const { carrier, mode } = formatCarrier(name); return mode ? `${carrier} · ${mode}` : carrier; };

  const filteredDrops = dropQuery.trim()
    ? dropDetails.filter(pk =>
        [pk.pickup_organisation, pk.pickup_poc, pk.pickup_city, pk.pickup_state, pk.pickup_postal_code, pk.pickup_address]
          .join(" ").toLowerCase().includes(dropQuery.trim().toLowerCase()))
    : dropDetails;
  // The chosen location leads the list — the first thing seen whenever the merchant comes back
  // to this step. The rest keep their saved order (sort is stable).
  const isDropSelected = (drop) => Boolean(selectedDrop && (selectedDrop.pickup_address_id != null ? selectedDrop.pickup_address_id === drop.pickup_address_id : selectedDrop === drop));
  const orderedDrops = selectedDrop ? [...filteredDrops].sort((a, b) => Number(isDropSelected(b)) - Number(isDropSelected(a))) : filteredDrops;

  const attachDropList = (el) => {
    dropListRef.current = el;
    if (dropListRO.current) { dropListRO.current.disconnect(); dropListRO.current = null; }
    if (el) {
      const ro = new ResizeObserver(updateDropEdges);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
      dropListRO.current = ro;
      updateDropEdges();
    }
  };
  const reached = stacked ? unlockedUpTo : highestStepReached;
  const pct = step === 6 ? 100 : Math.round(((reached - 1) / 5) * 100);
  const booked = step === 6 && responseData;
  useLayoutEffect(() => {
    if (!booked) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [booked]);

  /* ── Ticket data: the customer is the origin, the return location the destination ── */
  const originKnown = /^[0-9]{6}$/.test(String(cd.pincode || ""));
  const destKnown = reached >= 2 && selectedDrop?.pickup_postal_code;
  const ticketRows = [
    { k: "Customer", v: (cd.name || cd.phoneNumber) ? [cd.name, cd.phoneNumber].filter(Boolean).join(" · ") : null },
    { k: "Return to", v: destKnown ? (selectedDrop.pickup_organisation || selectedDrop.pickup_poc || selectedDrop.pickup_city) : null },
    {
      k: "Courier",
      v: reached >= 5
        ? (formData.courierDetails.useRecommendation ? "Smart allocation" : (chosenOption ? prettyCourier(chosenOption.carrier_partner_public_name) : null))
        : null,
    },
  ];
  const namedItems = od.itemDetails.filter(it => String(it.name || "").trim()).length;
  const ticketCells = [
    { k: reached >= 3 ? "Prepaid" : "Payment", v: reached >= 3 && invoiceValueNum > 0 ? fmtInr(od.invoiceValue) : null },
    { k: namedItems === 1 ? "Item" : "Items", v: namedItems ? String(namedItems) : null },
    {
      k: "Chargeable wt",
      v: billedKg != null ? `${billedKg.toFixed(2)} kg` : null,
      sub: billedKg != null && billedBasis ? (billedBasis === "vol" ? "volumetric" : "dead") : null,
    },
  ];
  const chargeValue = !formData.courierDetails.useRecommendation && chosenOption
    ? fmtInr(chosenOption.total_amount_charged_after_tax)
    : null;

  const settlementLabel = (() => {
    const t = String(resolvedSettlement || walletType || "").toUpperCase();
    if (!t) return null;
    if (t.includes("COD") || t.includes("NETOFF") || t.includes("NET_OFF")) return "COD net-off";
    if (t.includes("POSTPAID") || t.includes("POST_PAID")) return "your postpaid wallet";
    if (t.includes("PREPAID") || t.includes("PRE_PAID")) return "your prepaid wallet";
    return null;
  })();

  const bookedInfo = booked ? (() => {
    const name = responseData.courier_name || null;
    const opt = (name && courierOptions.find((o) => o.carrier_partner_public_name === name)) || chosenOption || null;
    const charge = opt && opt.total_amount_charged_after_tax != null ? Number(opt.total_amount_charged_after_tax) : null;
    const tat = opt && opt.max_tat_in_days != null ? Number(opt.max_tat_in_days) : null;
    const { carrier, mode } = formatCarrier(name || "");
    return { name, carrier, mode, accent: carrierAccent(carrier), charge, tat, edd: tat != null ? estDeliveryDate(tat) : null };
  })() : null;

  /* ── Step bodies (the desk side) ── */

  const renderCustomer = () => (
    <div className="cfo-stagger space-y-4">
      {/* Optional quick-fill: if the original went out on Wayuvega, its AWB fills the whole form —
          when it is a delivered forward order. Returns for anything else are simply filled in below. */}
      <JourneyGroup
        icon={ScanIcon}
        title="Quickfill from existing shipment"
        hint={<>Shipped the original on Wayuvega? Paste its AWB to fill in the return. Otherwise, fill in below.</>}
        done={awbFetched}
        right={(
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[#3877FC]/25 bg-white px-2.5 py-1 text-[11.5px] font-semibold text-[#2A59BD]" title="Fills the form from a Wayuvega shipment. Returns for other shipments are filled in below.">
            <SparklesIcon className="h-3.5 w-3.5" strokeWidth={2.5} /> Optional
          </span>
        )}
      >
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <ScanIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              name="awbNumber"
              value={awbNumber}
              onChange={(e) => setAwbNumber(e.target.value)}
              onBlur={(e) => setAwbNumber(e.target.value.trim())}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); if (!searchingAwb) searchOrder(); } }}
              placeholder="e.g. WVX8U204617091"
              autoComplete="off"
              spellCheck={false}
              aria-label="AWB of the shipment being returned"
              className={inputClass(false, "pl-10 pr-3 font-mono text-[13.5px] uppercase tracking-[0.04em] placeholder:font-sans placeholder:normal-case placeholder:tracking-normal")}
            />
          </div>
          <button type="button" onClick={() => searchOrder()} disabled={searchingAwb} className={`${BTN_PRIMARY} flex-shrink-0 justify-center`}>
            {searchingAwb ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> : <SearchIcon className="h-4 w-4" strokeWidth={2.5} />}
            {searchingAwb ? "Looking up…" : "Look up shipment"}
          </button>
        </div>
        <AnimatePresence initial={false}>
          {lookupCurrent && (
            <motion.div
              key={`${awbLookup.status}-${awbLookup.awb}`}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ height: { type: "spring", stiffness: 260, damping: 30 }, opacity: { duration: 0.18 } }}
              className="overflow-hidden"
            >
              <div className="pt-3.5"><OriginalShipmentStub lookup={awbLookup} onClear={clearAwbLookup} /></div>
            </motion.div>
          )}
        </AnimatePresence>
      </JourneyGroup>

      {/* Everything below locks while the looked-up shipment is refused (inert: no focus, no typing). */}
      <div
        inert={deskLocked || undefined}
        aria-hidden={deskLocked || undefined}
        className={`cfo-stagger space-y-4 transition-opacity duration-300 ${deskLocked ? "pointer-events-none select-none opacity-45" : ""}`}
      >
      <JourneyGroup icon={PhoneIcon} title="Contact" hint="Saved customers fill in from the phone number." done={contactDone}>
      <div className="grid gap-x-5 gap-y-3.5 md:grid-cols-2">
        <div>
          <label className={LABEL}>Phone number</label>
          <div className="relative">
            <PhoneIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              name="phoneNumber"
              placeholder="e.g. 9876543210"
              value={cd.phoneNumber}
              onChange={handlePhoneNumberChange}
              data-vkey="phoneNumber"
              aria-invalid={Boolean(E("phoneNumber"))}
              aria-describedby={E("phoneNumber") ? "err-phoneNumber" : undefined}
              className={inputClass(E("phoneNumber"), "pl-10 pr-10")}
            />
            {cd.phoneNumber && !E("phoneNumber") && cd.phoneNumber.length === 10 && (
              <span className="absolute right-3.5 top-1/2 -translate-y-1/2">
                <motion.span initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 500, damping: 24 }} className="block text-emerald-500">
                  <CheckIcon className="h-4 w-4" strokeWidth={3} />
                </motion.span>
              </span>
            )}
          </div>
          <FieldError id="err-phoneNumber" message={E("phoneNumber")} />
        </div>
        <div>
          <label className={LABEL}>Full name</label>
          <div className="relative">
            <UserIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              name="name"
              placeholder="e.g. Priya Sharma"
              value={cd.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, name: e.target.value } }))}
              onBlur={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, name: e.target.value.trim() } }))}
              data-vkey="name"
              aria-invalid={Boolean(E("name"))}
              aria-describedby={E("name") ? "err-name" : undefined}
              className={inputClass(E("name"), "pl-10")}
            />
          </div>
          <FieldError id="err-name" message={E("name")} />
        </div>
        <div>
          <label className={LABEL}>Email <span className="font-medium text-slate-400">· optional</span></label>
          <div className="relative">
            <MailIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="email"
              name="email"
              placeholder="e.g. priya@email.com"
              value={cd.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, email: e.target.value } }))}
              onBlur={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, email: e.target.value.trim() } }))}
              data-vkey="email"
              aria-invalid={Boolean(E("email"))}
              aria-describedby={E("email") ? "err-email" : undefined}
              className={inputClass(E("email"), "pl-10")}
            />
          </div>
          <FieldError id="err-email" message={E("email")} />
        </div>
      </div>
      </JourneyGroup>

      <JourneyGroup icon={MapPinIcon} title="Pickup address" hint="Where the courier collects the parcel from. We’ll fill the city and state from the pincode." done={addressDone} last>
      <div>
        <label className={LABEL}>Address</label>
        <textarea
          name="address"
          placeholder="e.g. 42 Green Park, Hauz Khas"
          value={cd.address}
          onChange={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, address: e.target.value } }))}
          onBlur={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, address: e.target.value.trim() } }))}
          rows="2"
          data-vkey="address"
          aria-invalid={Boolean(E("address"))}
          aria-describedby={E("address") ? "err-address" : undefined}
          className={`${inputClass(E("address"))} h-auto min-h-[68px] resize-none py-2.5 leading-relaxed`}
        />
        <FieldError id="err-address" message={E("address")} />
      </div>
      <div className="mt-4 grid gap-x-5 gap-y-3.5 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.1fr)_minmax(0,1.1fr)]">
        <div>
          <label className={LABEL}>Pincode</label>
          <div className="relative">
            <MapPinIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              name="pincode"
              placeholder="e.g. 400001"
              value={cd.pincode}
              onChange={handlePincodeChange}
              maxLength="6"
              data-vkey="pincode"
              aria-invalid={Boolean(E("pincode"))}
              aria-describedby={E("pincode") ? "err-pincode" : undefined}
              className={inputClass(E("pincode"), "pl-10 pr-10")}
            />
            {loadingPincode && (
              <span className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2">
                <span className="block h-full w-full animate-spin rounded-full border-2 border-slate-200 border-t-[#3877FC]" />
              </span>
            )}
          </div>
          {loadingPincode ? <p className="mt-1.5 text-[12px] font-medium text-[#3877FC]">Finding the city and state…</p> : null}
          {!loadingPincode && pincodeLookupFailed ? (
            <p className="mt-1.5 text-[12px] font-medium text-amber-700">We couldn’t find this pincode — please add the city and state below.</p>
          ) : null}
          <FieldError id="err-pincode" message={E("pincode")} />
        </div>
        <div>
          <label className={LABEL}>City</label>
          <input
            type="text"
            name="city"
            value={cd.city}
            onChange={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, city: e.target.value } }))}
            onBlur={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, city: e.target.value.trim() } }))}
            data-vkey="city"
            aria-invalid={Boolean(E("city"))}
            aria-describedby={E("city") ? "err-city" : undefined}
            className={inputClass(E("city"), cd.city ? "cfo-wash" : "")}
            placeholder="From pincode"
          />
          <FieldError id="err-city" message={E("city")} />
        </div>
        <div>
          <label className={LABEL}>State</label>
          <input
            type="text"
            name="state"
            value={cd.state}
            onChange={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, state: e.target.value } }))}
            onBlur={(e) => setFormData((prev) => ({ ...prev, pickupDetails: { ...prev.pickupDetails, state: e.target.value.trim() } }))}
            data-vkey="state"
            aria-invalid={Boolean(E("state"))}
            aria-describedby={E("state") ? "err-state" : undefined}
            className={inputClass(E("state"), cd.state ? "cfo-wash" : "")}
            placeholder="From pincode"
          />
          <FieldError id="err-state" message={E("state")} />
        </div>
      </div>
      </JourneyGroup>
      </div>
    </div>
  );

  const renderReturnTo = () => (
    <div className="cfo-stagger space-y-4">
      <JourneyGroup
        icon={StoreIcon}
        title="Return location"
        done={dropDone}
        last
        hint={dropDetails.length ? `${dropDetails.length} saved · the parcel is delivered back here` : null}
        right={(
          <>
        {dropDetails.length > 6 && (
          <div className="relative min-w-0 flex-1 sm:flex-none">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={dropQuery}
              onChange={(e) => setDropQuery(e.target.value)}
              placeholder="Search name, city or pincode"
              className="h-9 w-full rounded-xl border border-[#D6DEE9] bg-white pl-9 pr-3 text-[13px] sm:w-60 font-medium text-slate-900 placeholder:text-slate-400 outline-none transition-all hover:border-[#AFBDD1] focus:border-[#3877FC] focus:ring-4 focus:ring-[#3877FC]/10"
            />
          </div>
        )}
          <button
            type="button"
            onClick={refreshDropLocations}
            title="Refresh locations"
            aria-busy={refreshingDrops}
            className={`grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg border bg-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 ${refreshingDrops ? "border-[#3877FC]/40 text-[#3877FC]" : "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700"}`}
          >
            <RefreshIcon className={`h-4 w-4 ${refreshingDrops ? "animate-spin" : ""}`} />
          </button>
          <button
            type="button"
            onClick={openAddAddress}
            className="inline-flex h-9 flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#3877FC] px-3.5 text-[13px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_1px_2px_rgba(15,23,42,0.10),0_6px_16px_-8px_rgba(56,119,252,0.55)] transition-all hover:bg-[#2A59BD] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40 focus-visible:ring-offset-2"
          >
            <PlusIcon className="h-4 w-4" strokeWidth={2.5} /> Add return location
          </button>
          </>
        )}
      >
      {E("drop") ? (
        <p className="mb-4 rounded-lg border border-rose-200/70 bg-rose-50 px-3.5 py-2.5 text-[12.5px] font-semibold text-rose-700">
          Choose a return location to continue.
        </p>
      ) : null}
      {dropDetails.length > 0 ? (
        filteredDrops.length > 0 ? (
          <div className="relative">
          <div ref={attachDropList} onScroll={updateDropEdges} className="custom-scrollbar max-h-[min(52vh,560px)] overflow-y-auto pr-1.5">
          <div className="columns-1 gap-3 md:columns-2">
            {orderedDrops.map((drop, di) => {
              const rowId = drop.pickup_address_id ?? `row-${di}`;
              const isSelected = isDropSelected(drop);
              const locName = displayLocName(drop.pickup_organisation || drop.pickup_poc);
              return (
                <motion.div key={rowId} layout transition={{ type: "spring", stiffness: 380, damping: 32 }} className="group relative mb-3 break-inside-avoid">
                  <label
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-all duration-150 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#3877FC]/30 ${
                      isSelected
                        ? "border-[#3877FC] bg-[#3877FC]/[0.04] shadow-[0_2px_10px_-4px_rgba(56,119,252,0.35)]"
                        : "border-slate-200 bg-white hover:border-[#AFBDD1] hover:shadow-sm"
                    }`}
                  >
                    <input
                      type="radio"
                      name="returnLocation"
                      className="sr-only"
                      checked={Boolean(isSelected)}
                      onChange={() => { setSelectedDrop(drop); dropListRef.current?.scrollTo({ top: 0, behavior: "smooth" }); }}
                    />
                    <motion.span
                      layout
                      transition={{ type: "spring", stiffness: 500, damping: 26 }}
                      className={`mt-0.5 grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg text-[12px] font-bold tracking-wide transition-colors duration-150 ${
                        isSelected ? "bg-[#3877FC] text-white shadow-[0_2px_8px_-2px_rgba(56,119,252,0.5)]" : "bg-[#3877FC]/[0.08] text-[#2A59BD]"
                      }`}
                      aria-hidden="true"
                    >
                      {isSelected ? <CheckIcon className="h-4 w-4" strokeWidth={3} /> : locInitials(locName)}
                    </motion.span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="min-w-0 text-[13.5px] font-semibold leading-snug text-slate-900">{locName}</span>
                        <span className="flex flex-shrink-0 items-center gap-1">
                          <span className="font-mono text-[12px] font-semibold tabular-nums text-slate-700">{drop.pickup_postal_code}</span>
                          {drop.pickup_address_id != null && (
                            <button
                              type="button"
                              aria-label={`Options for ${locName}`}
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDropMenuFor(dropMenuFor === rowId ? null : rowId); }}
                              className="grid h-6 w-6 -translate-y-0.5 cursor-pointer place-items-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
                            >
                              <MoreVerticalIcon className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </span>
                      </span>
                      <span className="mt-0.5 block text-[12px] leading-snug text-slate-500">
                        {titleCaseLoc(drop.pickup_address)}
                      </span>
                      <span className="mt-1 block text-[11.5px] leading-snug text-slate-400">
                        <span className="font-medium text-slate-500">{[titleCaseLoc(drop.pickup_city), titleCaseLoc(drop.pickup_state)].filter(Boolean).join(", ")}</span>
                        {drop.pickup_poc ? ` · ${drop.pickup_poc}` : ""}{drop.pickup_poc_contact_number ? ` · ${drop.pickup_poc_contact_number}` : ""}
                      </span>
                      {drop.__fromOrder && (
                        <span className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-[#3877FC]/[0.08] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#2A59BD]">
                          <ScanIcon className="h-3 w-3" /> From the original order
                        </span>
                      )}
                    </span>
                  </label>
                  {dropMenuFor === rowId && (
                    <>
                      <button type="button" aria-label="Close menu" className="fixed inset-0 z-10 cursor-default" onClick={() => setDropMenuFor(null)} />
                      <div className="absolute right-2 top-9 z-20 w-32 overflow-hidden rounded-lg border border-[#D6DEE9] bg-white p-1 shadow-lg">
                        <button
                          type="button"
                          onClick={() => { setDropMenuFor(null); openEditAddress(drop); }}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-[12.5px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-[#2A59BD]"
                        >
                          <PencilIcon className="h-3.5 w-3.5" /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => { setDropMenuFor(null); openDeleteAddress(drop.pickup_address_id); }}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-[12.5px] font-semibold text-rose-600 transition-colors hover:bg-rose-50"
                        >
                          <TrashIcon className="h-3.5 w-3.5" /> Delete
                        </button>
                      </div>
                    </>
                  )}
                </motion.div>
              );
            })}
          </div>
          </div>
          <div aria-hidden="true" className={`pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-[#FAFBFD] via-[#FAFBFD]/70 to-transparent transition-opacity duration-200 ${dropEdges.top ? "opacity-0" : "opacity-100"}`} />
          <div aria-hidden="true" className={`pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#FAFBFD] via-[#FAFBFD]/70 to-transparent transition-opacity duration-200 ${dropEdges.bottom ? "opacity-0" : "opacity-100"}`} />
          </div>
        ) : (
          <p className="text-[13.5px] text-slate-500">No saved location matches “{dropQuery.trim()}”.</p>
        )
      ) : (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-300 px-6 py-10 text-center">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-slate-100"><StoreIcon className="h-6 w-6 text-slate-400" /></span>
          <p className="mt-1 text-[14.5px] font-semibold text-slate-800">No return locations yet</p>
          <p className="text-[13px] text-slate-500">Add the address the parcel should be delivered back to.</p>
          <button type="button" onClick={openAddAddress} className={`${BTN_PRIMARY} mt-3`}>
            + Add return location
          </button>
        </div>
      )}
      </JourneyGroup>
    </div>
  );

  const renderOrder = () => (
    <div className="cfo-stagger space-y-4">
      <JourneyGroup icon={RupeeIcon} title="Payment" done={paymentDone}>
      <div className="flex flex-wrap items-start gap-x-5 gap-y-4">
        <div>
          <label className={LABEL}>Payment mode</label>
          <div className="inline-flex h-11 items-center gap-2 rounded-xl border border-[#E2EAF8] bg-[#F6F9FF] px-4 text-[13px] font-semibold text-slate-800">
            <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white"><CheckIcon className="h-3 w-3" strokeWidth={3.5} /></span>
            Prepaid
            <span className="font-medium text-slate-400">· returns are always prepaid</span>
          </div>
        </div>
        <div className="w-44">
          <label className={LABEL}>Invoice value <span className="font-medium text-slate-400">· incl. GST</span></label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[14px] font-medium text-slate-400">₹</span>
            <input
              type="number" onKeyDown={numericKeyGuard()} onPaste={numericPasteGuard()}
              value={od.invoiceValue === 0 ? "" : od.invoiceValue}
              onChange={(e) => setFormData((prev) => ({ ...prev, orderDetails: { ...prev.orderDetails, invoiceValue: e.target.value } }))}
              data-vkey="invoiceValue"
              aria-invalid={Boolean(E("invoiceValue"))}
              aria-describedby={E("invoiceValue") ? "err-invoiceValue" : undefined}
              className={inputClass(E("invoiceValue"), "pl-8 text-right tabular-nums")}
              placeholder="e.g. 1500"
            />
          </div>
          <FieldError id="err-invoiceValue" message={E("invoiceValue")} />
        </div>
      </div>
      </JourneyGroup>

      <JourneyGroup icon={FileTextIcon} title="Order" done={orderDone}>
      {/* The reference gets the whole row: merchant references are often a UUID, and the quick-fill
          appends "_REVERSE" — 44 mono characters must sit in view at any desktop width. */}
      <div className="grid gap-y-3.5">
        <div>
          <label className={LABEL}>Order reference</label>
          <div className="relative">
            <input
              type="text"
              value={od.clientOrderId}
              spellCheck={false}
              autoComplete="off"
              onChange={(e) => setFormData((prev) => ({ ...prev, orderDetails: { ...prev.orderDetails, clientOrderId: e.target.value } }))}
              onBlur={(e) => setFormData((prev) => ({ ...prev, orderDetails: { ...prev.orderDetails, clientOrderId: e.target.value.trim() } }))}
              data-vkey="clientOrderId"
              aria-invalid={Boolean(E("clientOrderId")) || orderRefBlocked}
              aria-describedby={E("clientOrderId") ? "err-clientOrderId" : orderRefBlocked ? "ref-block" : undefined}
              className={inputClass(Boolean(E("clientOrderId")) || orderRefBlocked, "pr-11 font-mono text-[12.5px]")}
              placeholder="e.g. SHOP-1042_REVERSE"
            />
            <button
              type="button"
              onClick={handleGenerateOrderId}
              title="Generate a reference"
              aria-label="Generate a reference"
              className="absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 cursor-pointer place-items-center rounded-lg text-[#2A59BD] transition-all hover:scale-110 active:scale-[0.92] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
            >
              <SparklesIcon className="h-4 w-4" />
            </button>
          </div>
          <FieldError id="err-clientOrderId" message={E("clientOrderId")} />
          {orderRefBlockInfo ? (
            <div id="ref-block" className="mt-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2" role="alert">
              <p className="text-[12px] font-medium leading-snug text-rose-700">{orderRefBlockInfo.msg}</p>
              <a href={orderRefBlockInfo.href} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex cursor-pointer items-center gap-1 text-[11.5px] font-semibold text-[#2A59BD] transition-colors hover:text-[#1E40AF] hover:underline">
                {orderRefBlockInfo.linkLabel} <ExternalLinkIcon className="h-3 w-3" />
              </a>
            </div>
          ) : orderRefFailedBefore ? (
            <p className="mt-1.5 text-[11.5px] font-medium text-amber-700">An earlier attempt with this reference didn't go through — booking again retries it. Change the reference only if this is a second return.</p>
          ) : (
            <p className="mt-1.5 text-[11.5px] text-slate-500">Your own ID for this return. Fetching by AWB suggests the original reference with “_REVERSE”.</p>
          )}
        </div>
        <div>
          {showShippingDetails ? (
            <div className="md:max-w-sm">
              <label className={LABEL}>Invoice number <span className="font-medium text-slate-400">· optional</span></label>
              <input
                type="text"
                value={od.invoiceNumber || ""}
                onChange={(e) => setFormData((prev2) => ({ ...prev2, orderDetails: { ...prev2.orderDetails, invoiceNumber: e.target.value } }))}
                onBlur={(e) => setFormData((prev2) => ({ ...prev2, orderDetails: { ...prev2.orderDetails, invoiceNumber: e.target.value.trim() } }))}
                className={inputClass(false)}
                placeholder="e.g. INV-2043"
              />
            </div>
          ) : (
            <div>
              <button
                type="button"
                onClick={() => setShowShippingDetails((v) => !v)}
                disabled={orderRefBlocked}
                title={orderRefBlocked ? "Sort out the order reference first" : undefined}
                className="inline-flex h-11 cursor-pointer items-center rounded-xl border border-dashed border-[#3877FC]/35 bg-[#3877FC]/[0.03] px-4 text-[13px] font-semibold text-[#2A59BD] transition-all hover:border-[#3877FC]/60 hover:bg-[#3877FC]/[0.07] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[#3877FC]/35 disabled:hover:bg-[#3877FC]/[0.03]"
              >
                + Add invoice number&nbsp;<span className="font-medium text-[#2A59BD]/55">· optional</span>
              </button>
            </div>
          )}
        </div>
      </div>
      </JourneyGroup>

      <JourneyGroup icon={TagIcon} title="Items being returned" done={itemsDone} last>
      {orderRefBlocked && (
        <p className="mb-2.5 text-[12px] font-medium text-slate-500">Sort out the order reference above before the items.</p>
      )}
      <div className={`cfo-thin-scroll overflow-x-auto ${orderRefBlocked ? "pointer-events-none select-none opacity-50" : ""}`} aria-disabled={orderRefBlocked}>
        <div className="min-w-[560px]">
          <div className="overflow-hidden rounded-xl border border-[#D6DEE9] bg-white shadow-[0_1px_2px_rgba(42,89,189,0.05)]">
          <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_84px_120px_32px] gap-3 border-b border-[#E7ECF3] bg-[#3877FC]/[0.06] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            <span className="pl-3.5">Item</span>
            <span className="pl-3.5">SKU{cloneMode ? <span className="text-slate-500"> · optional</span> : null}</span>
            <span className="pr-3.5 text-right">Qty</span>
            <span className="pr-3.5 text-right">Unit price</span>
            <span aria-hidden="true" />
          </div>
          <div className="divide-y divide-[#E7ECF3]">
            {od.itemDetails.map((item, index) => (
              <div key={index} className="bg-[#F6F9FF] px-3 py-2.5 transition-colors hover:bg-[#EEF4FE] focus-within:bg-[#E7EFFE]">
                <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_84px_120px_32px] items-start gap-3">
                  <div>
                    <textarea
                      rows={1}
                      value={item.name}
                      onChange={(e) => handleItemChange(index, "name", e.target.value.replace(/\s*\n\s*/g, " "))}
                      onBlur={(e) => handleItemChange(index, "name", e.target.value.trim())}
                      onKeyDown={(e) => { if (e.key === "Enter" && e.shiftKey) e.preventDefault(); }}
                      ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`; } }}
                      aria-label="Item name"
                      data-vkey={`name-${index}`}
                      aria-invalid={Boolean(E(`name-${index}`))}
                      aria-describedby={E(`name-${index}`) ? `err-name-${index}` : undefined}
                      className={`w-full resize-none overflow-hidden rounded-xl border bg-white px-3.5 py-[11px] text-sm font-medium leading-[20px] text-slate-900 outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-slate-400 focus:border-[#3877FC] focus:shadow-[0_0_0_4px_rgba(56,119,252,0.12)] ${E(`name-${index}`) ? "border-rose-300 bg-rose-50/40" : "border-[#D6DEE9] hover:border-[#AFBDD1]"}`}
                      placeholder="e.g. Cotton kurta set"
                    />
                    <FieldError id={`err-name-${index}`} message={E(`name-${index}`)} />
                  </div>
                  <div>
                    <div className="relative">
                      <textarea
                        rows={1}
                        value={item.skuId}
                        onChange={(e) => handleItemChange(index, "skuId", e.target.value.replace(/\s*\n\s*/g, ""))}
                        onBlur={(e) => handleItemChange(index, "skuId", e.target.value.trim())}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (!e.shiftKey) fetchSKUDetails(index, String(item.skuId || "").trim()); } }}
                        ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`; } }}
                        aria-label="SKU"
                        data-vkey={`skuId-${index}`}
                        aria-invalid={Boolean(E(`skuId-${index}`))}
                        aria-describedby={E(`skuId-${index}`) ? `err-skuId-${index}` : undefined}
                        className={`w-full resize-none overflow-hidden rounded-xl border bg-white px-3.5 py-[12px] font-mono text-[12.5px] font-medium leading-[18px] text-slate-900 outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-slate-400 focus:border-[#3877FC] focus:shadow-[0_0_0_4px_rgba(56,119,252,0.12)] pr-[68px] ${E(`skuId-${index}`) ? "border-rose-300 bg-rose-50/40" : "border-[#D6DEE9] hover:border-[#AFBDD1]"}`}
                        placeholder="e.g. SKU-1125"
                      />
                      <span className="absolute right-1.5 top-1.5 flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => fetchSKUDetails(index, String(item.skuId || "").trim())}
                          disabled={!String(item.skuId || "").trim()}
                          title="Fill in from your catalogue"
                          aria-label="Fill in from your catalogue"
                          className="grid h-8 w-8 cursor-pointer place-items-center rounded-md text-slate-500 transition-all hover:scale-110 hover:text-[#2A59BD] active:scale-[0.92] disabled:cursor-default disabled:opacity-35 disabled:hover:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
                        >
                          <SearchIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleItemChange(index, "skuId", generateRandomSku())}
                          title="Generate a random SKU"
                          aria-label="Generate a random SKU"
                          className="grid h-8 w-8 cursor-pointer place-items-center rounded-md text-[#2A59BD] transition-all hover:scale-110 active:scale-[0.92] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
                        >
                          <SparklesIcon className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </div>
                    <FieldError id={`err-skuId-${index}`} message={E(`skuId-${index}`)} />
                  </div>
                  <div>
                    <input
                      type="number" onKeyDown={numericKeyGuard(true)} onPaste={numericPasteGuard(true)}
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                      onFocus={(e) => e.target.select()}
                      aria-label="Quantity"
                      data-vkey={`quantity-${index}`}
                      aria-invalid={Boolean(E(`quantity-${index}`))}
                      aria-describedby={E(`quantity-${index}`) ? `err-quantity-${index}` : undefined}
                      className={inputClass(E(`quantity-${index}`), "text-right tabular-nums")}
                      placeholder="e.g. 1"
                      min="1"
                    />
                    <FieldError id={`err-quantity-${index}`} message={E(`quantity-${index}`)} />
                  </div>
                  <div>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] font-medium text-slate-400">₹</span>
                      <input
                        type="number" onKeyDown={numericKeyGuard()} onPaste={numericPasteGuard()}
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(index, "unitPrice", e.target.value)}
                        aria-label="Unit price"
                        data-vkey={`unitPrice-${index}`}
                        aria-invalid={Boolean(E(`unitPrice-${index}`))}
                        aria-describedby={E(`unitPrice-${index}`) ? `err-unitPrice-${index}` : undefined}
                        className={inputClass(E(`unitPrice-${index}`), "pl-7 text-right tabular-nums")}
                        placeholder="e.g. 100"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <FieldError id={`err-unitPrice-${index}`} message={E(`unitPrice-${index}`)} />
                  </div>
                  <div>
                    <button
                      type="button"
                      tabIndex={-1}
                      title={od.itemDetails.length > 1 ? "Remove this item" : "Clear this item"}
                      aria-label={od.itemDetails.length > 1 ? `Remove item ${index + 1}` : "Clear this item"}
                      onClick={() => removeItemDetail(index)}
                      className="mt-1.5 grid h-8 w-8 cursor-pointer place-items-center rounded-lg text-slate-400 transition-all hover:bg-rose-50 hover:text-rose-600 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/40"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {/* meta band - one level floor: HSN left, line total on the money axis */}
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2">
                    {expandedHSNItems[index] ? (
                      <span className="flex items-center gap-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">HSN</span>
                        <input
                          type="text"
                          value={item.hsnCode || ""}
                          onChange={(e) => handleItemChange(index, "hsnCode", e.target.value)}
                          onBlur={(e) => handleItemChange(index, "hsnCode", e.target.value.trim())}
                          aria-label="HSN code"
                          className="h-7 w-44 rounded-lg border border-[#D6DEE9] bg-white px-2.5 font-mono text-[11.5px] font-medium text-slate-900 outline-none transition-all placeholder:text-slate-400 hover:border-[#AFBDD1] focus:border-[#3877FC] focus:ring-2 focus:ring-[#3877FC]/10"
                          placeholder="e.g. 610910"
                        />
                        <button
                          type="button"
                          aria-label="Hide HSN code"
                          title="Hide HSN code"
                          onClick={() => setExpandedHSNItems(prev2 => ({ ...prev2, [index]: false }))}
                          className="grid h-6 w-6 cursor-pointer place-items-center rounded text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setExpandedHSNItems(prev2 => ({ ...prev2, [index]: !prev2[index] }))}
                        className={`inline-flex h-6 cursor-pointer items-center rounded-md border px-2 text-[10.5px] font-semibold transition-colors ${item.hsnCode ? "border-solid border-[#D6DEE9] bg-white text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.06)] hover:border-[#AFBDD1] hover:text-[#2A59BD]" : "border-dashed border-[#3877FC]/35 bg-[#3877FC]/[0.04] text-[#2A59BD] hover:border-[#3877FC]/60 hover:bg-[#3877FC]/[0.08]"}`}
                      >
                        {item.hsnCode ? `HSN ${item.hsnCode}` : (<>+ HSN&nbsp;<span className="font-medium text-[#2A59BD]/55">· optional</span></>)}
                      </button>
                    )}
                  </span>
                  {(Number(item.quantity) || 0) * (Number(item.unitPrice) || 0) > 0 ? (
                    <span className="flex items-center gap-1.5 pr-3.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Line total</span>
                      <span className="flex flex-col items-end leading-tight">
                        <span className="text-[12.5px] font-semibold tabular-nums text-slate-700">{fmtInr((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0))}</span>
                        <span className="text-[9px] font-medium text-slate-400">(incl. GST)</span>
                      </span>
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-[#E7ECF3] bg-[#F6F9FF] px-3 py-2">
            <button
              type="button"
              onClick={() => {
                const nextIndex = od.itemDetails.length;
                addItemDetail();
                requestAnimationFrame(() => document.querySelector(`[data-vkey="name-${nextIndex}"]`)?.focus());
              }}
              className="flex h-9 w-full cursor-pointer items-center justify-center rounded-lg border border-dashed border-[#B9C6DA] bg-white text-[12.5px] font-semibold text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all hover:border-[#3877FC]/50 hover:text-[#2A59BD] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
            >
              + Add another item
            </button>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-[#E7ECF3] bg-[#3877FC]/[0.04] px-3 py-2.5 pr-[26px]">
            <span className="text-[13px] text-slate-500">Items subtotal</span>
            <span className="flex flex-col items-end leading-tight">
              <b className={`tabular-nums ${itemsSubtotal > 0 ? "text-[15.5px] font-semibold text-slate-900" : "text-[13px] font-medium text-slate-600"}`}>{fmtInr(itemsSubtotal)}</b>
              {itemsSubtotal > 0 ? <span className="text-[10px] font-medium text-slate-400">(incl. GST)</span> : null}
            </span>
          </div>
          </div>
        </div>
      </div>
      {invoiceMismatch && (
        <p className="rounded-lg border border-amber-200/70 bg-amber-50 px-3.5 py-2.5 text-[12.5px] font-medium leading-relaxed text-amber-800">
          Items add up to {fmtInr(itemsSubtotal)}, but the invoice value is {fmtInr(od.invoiceValue)}. Both are sent as entered.
        </p>
      )}
      </JourneyGroup>
    </div>
  );

  const dimFields = [
    { key: "weight", label: "Dead weight", placeholder: "e.g. 500", options: ["gm", "kg"] },
    { key: "length", label: "Length", placeholder: "e.g. 30", options: ["cm", "inch"] },
    { key: "breadth", label: "Breadth", placeholder: "e.g. 20", options: ["cm", "inch"] },
    { key: "height", label: "Height", placeholder: "e.g. 10", options: ["cm", "inch"] },
  ];

  const renderPackage = () => (
    <div className="cfo-stagger space-y-4">
      <JourneyGroup icon={BoxIcon} title="Weight and size" hint="Packed weight on the scale, outer dimensions of the box the customer hands over." done={weightDone} last>
      <div className="grid grid-cols-2 gap-x-5 gap-y-4 lg:grid-cols-4">
        {dimFields.map(({ key, label, placeholder, options }) => (
          <div key={key}>
            <label className={LABEL}>{label}</label>
            <div className={`flex h-11 items-stretch overflow-hidden rounded-xl border bg-white transition-[border-color,box-shadow] duration-150 focus-within:border-[#3877FC] focus-within:shadow-[0_0_0_4px_rgba(56,119,252,0.12)] ${
              E(key) ? "border-rose-300 bg-rose-50/40" : "border-[#D6DEE9] hover:border-[#AFBDD1]"
            }`}>
              <input
                type="number" onKeyDown={numericKeyGuard()} onPaste={numericPasteGuard()}
                placeholder={placeholder}
                value={pd[key]}
                onChange={(e) => handlePackageValueChange(key, e.target.value)}
                aria-label={label}
                data-vkey={key}
                aria-invalid={Boolean(E(key))}
                aria-describedby={E(key) ? `err-${key}` : undefined}
                className="min-w-0 flex-1 bg-transparent px-3.5 text-[14px] font-semibold tabular-nums text-slate-900 placeholder:font-medium placeholder:text-slate-500 focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <select
                value={units[key]}
                onChange={(e) => handlePackageUnitChange(key, e.target.value)}
                aria-label={`${label} unit`}
                className="cursor-pointer border-l border-slate-200/80 bg-transparent px-2.5 text-[12px] font-medium text-slate-500 focus:outline-none"
              >
                {options.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <FieldError id={`err-${key}`} message={E(key)} />
          </div>
        ))}
      </div>

      <AnimatePresence initial={false}>
      {(pd.weight || pd.length || pd.breadth || pd.height) && (
        <motion.div
          key="package-studio"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ height: { type: "spring", stiffness: 150, damping: 26, mass: 1 }, opacity: { duration: 0.34, ease: "easeOut" } }}
          className="overflow-clip"
        >
          <PackageStudio
            lCm={convertLength(pd.length, units.length)}
            bCm={convertLength(pd.breadth, units.breadth)}
            hCm={convertLength(pd.height, units.height)}
            l={Number(pd.length) || 0} b={Number(pd.breadth) || 0} h={Number(pd.height) || 0}
            unitL={units.length} unitB={units.breadth} unitH={units.height}
            weightDisp={Number(pd.weight) || 0} unitW={units.weight}
            deadKg={deadKg} volKg={volKg} billedBasis={billedBasis} billedKg={billedKg}
          />
        </motion.div>
      )}
      </AnimatePresence>
      </JourneyGroup>
    </div>
  );

  const renderCourier = () => (
    <div className="cfo-stagger space-y-4">
      <SmartAllocationEngine
        on={formData.courierDetails.useRecommendation}
        onToggle={() => handleCourierSelection(!formData.courierDetails.useRecommendation)}
      />

      <AnimatePresence initial={false}>
        {!formData.courierDetails.useRecommendation && (
          <motion.div
            key="chooser"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ height: SPRING, opacity: { duration: 0.16 } }}
            className="overflow-clip"
          >
            <div className="pt-4">
              <CourierPicker
                loading={loadingCouriers}
                sortedOptions={sortedOptions}
                serviceableOptions={serviceableOptions}
                serviceableDisplay={serviceableDisplay}
                unserviceableOptions={unserviceableOptions}
                showUnserviceable={showUnserviceable}
                onToggleUnserviceable={() => setShowUnserviceable(v => !v)}
                courierSort={courierSort}
                onSortChange={setCourierSort}
                courierRoute={courierRoute}
                cheapestPrice={cheapestPrice}
                fastestTat={fastestTat}
                selected={formData.courierDetails.courierPartner}
                onSelect={(name) => setFormData((prev) => ({ ...prev, courierDetails: { ...prev.courierDetails, courierPartner: name } }))}
                serviceableOf={serviceableOf}
                paymentLabel="reverse pickup"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const STEP_META = [
    { key: "pickupDetails", title: "Customer", sub: "Who is sending it back?", body: renderCustomer },
    { key: "dropDetails", title: "Return to", sub: "Where should it come back to?", body: renderReturnTo },
    { key: "orderDetails", title: "Order & items", sub: "What's being returned?", body: renderOrder },
    { key: "packageDetails", title: "Package", sub: "How big and heavy is the package?", body: renderPackage },
    { key: "courierDetails", title: "Courier", sub: "Who should collect it?", body: renderCourier },
  ];
  const prevStepRef = useRef(step);
  const stepDir = step >= prevStepRef.current ? 1 : -1;
  useEffect(() => { prevStepRef.current = step; }, [step]);
  const courierDone = formData.courierDetails.useRecommendation || Boolean(formData.courierDetails.courierPartner);
  const STEP_GROUPS = [
    [contactDone, addressDone],
    [dropDone],
    [paymentDone, orderDone, itemsDone],
    [weightDone],
    [courierDone],
  ];
  const groupsOfStep = STEP_GROUPS[step - 1] || [];
  const groupsDone = groupsOfStep.filter(Boolean).length;
  const stepReady = groupsOfStep.length > 0 && groupsDone === groupsOfStep.length;
  const RING_C = 2 * Math.PI * 13;
  const FormBody = step <= 4 ? "form" : "div";
  const SECTION_ICON = { pickupDetails: UserIcon, dropDetails: StoreIcon, orderDetails: RupeeIcon, packageDetails: BoxIcon, courierDetails: TruckIcon };
  const renderSectionHeader = (n) => {
    const sm = STEP_META[n - 1];
    const Icon = SECTION_ICON[sm.key] || UserIcon;
    const groups = STEP_GROUPS[n - 1] || [];
    const done = groups.filter(Boolean).length;
    const complete = groups.length > 0 && done === groups.length;
    const title = !stacked && n < highestStepReached ? `Editing · ${sm.title}` : sm.title;
    const showRing = !(stacked && n > unlockedUpTo);
    return (
      <div className="mb-4 flex items-center gap-3">
        <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-2xl bg-[#3877FC] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_10px_15px_-3px_rgba(56,119,252,0.25),0_4px_6px_-4px_rgba(56,119,252,0.25)]">
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h2 className="text-[17px] font-semibold tracking-[-0.015em] text-slate-900">{title}</h2>
          <p className="mt-0.5 text-[13px] text-slate-500">{sm.sub}</p>
        </div>
        {showRing && <span className="relative ml-auto grid h-[38px] w-[38px] flex-shrink-0 place-items-center" title={`${done} of ${groups.length} complete`}>
          <svg width="38" height="38" viewBox="0 0 38 38" className="-rotate-90">
            <circle cx="19" cy="19" r="13" fill="none" stroke="#E2E8F0" strokeWidth="3.5" />
            <motion.circle
              cx="19" cy="19" r="13" fill="none"
              stroke={complete ? "#10B981" : "#3877FC"}
              strokeWidth="3.5" strokeLinecap="round"
              strokeDasharray={RING_C}
              initial={false}
              animate={{ strokeDashoffset: RING_C * (1 - (groups.length ? done / groups.length : 0)) }}
              transition={{ type: "spring", stiffness: 90, damping: 20, delay: 0.14 }}
            />
          </svg>
          <span className={`absolute flex items-baseline overflow-hidden text-[10px] font-semibold tabular-nums ${complete ? "text-emerald-600" : "text-slate-500"}`}>
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span key={done} initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -8, opacity: 0 }} transition={{ duration: 0.2, ease: "easeOut" }}>
                {done}
              </motion.span>
            </AnimatePresence>
            /{groups.length}
          </span>
        </span>}
      </div>
    );
  };

  /* ── The page ── */
  return (
    <MotionConfig reducedMotion="user">
      <div
        className="relative min-h-full bg-[#F6F8FB]"
        style={{
          backgroundImage: "radial-gradient(1100px 440px at 55% -160px, rgba(56,119,252,0.06), transparent 70%), radial-gradient(900px 420px at 6% 110%, rgba(56,119,252,0.035), transparent 70%)",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
        }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(rgba(30,58,109,0.05) 1px, transparent 1.4px)",
            backgroundSize: "22px 22px",
            maskImage: "linear-gradient(to bottom, black, transparent 400px)",
            WebkitMaskImage: "linear-gradient(to bottom, black, transparent 400px)",
          }}
        />
        <div className="relative mx-auto w-full max-w-[1440px] px-5 pb-4 pt-3 lg:px-8 antialiased">
          {booked ? (
            <BookedSuccess
              variant="return"
              bookedAt={bookedAt}
              origin={{ city: cd.city, pin: cd.pincode }}
              destination={{ city: selectedDrop?.pickup_city, pin: selectedDrop?.pickup_postal_code }}
              courier={bookedInfo}
              charge={bookedInfo?.charge ?? null}
              settlement={settlementLabel}
              billedKg={billedKg}
              billedBasis={billedBasis}
              order={{
                recipientName: cd.name,
                recipientPhone: cd.phoneNumber,
                paymentLabel: "Prepaid",
                paymentValue: invoiceValueNum > 0 ? fmtInr(od.invoiceValue) : null,
                itemsCount: namedItems,
                orderRef: responseData.order_reference_number,
                waybill: responseData.waybill,
              }}
              labelUrl={responseData.label_url}
              invoiceUrl={responseData.commercial_invoice_url}
              awbCopied={awbCopied}
              onCopyAwb={() => {
                navigator.clipboard?.writeText(String(responseData.waybill));
                setAwbCopied(true);
                window.setTimeout(() => setAwbCopied(false), 1500);
              }}
              onViewDetails={() => responseData.order_reference_number && navigate(`/shipment-details/${encodeURIComponent(responseData.order_reference_number)}`)}
              onAllShipments={() => navigate("/all-shipments")}
              onBookAnother={resetForm}
            />
          ) : (
            <div className="gap-6 lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
              <motion.div
                key={layoutMode}
                ref={flowRef}
                layout
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className={`${stacked ? "" : CARD} scroll-mt-[76px]`}
                onBlurCapture={handleFieldBlur}
              >
                {!stacked && (
                <div ref={stepRowRef} className="cfo-thin-scroll sticky top-16 z-30 flex w-full items-center justify-between gap-1 overflow-x-auto rounded-t-2xl border-b border-slate-100 bg-white/95 px-5 py-2 backdrop-blur lg:px-7" aria-label="Sections">
                  {/* the desk's badge: this one runs backwards */}
                  <span className="mr-1 inline-flex flex-shrink-0 items-center gap-1 rounded-md bg-[#3877FC]/[0.08] px-2 py-1 text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#2A59BD]" title="Reverse pickup — collected from the customer, delivered back to you">
                    <ReturnIcon className="h-3 w-3" strokeWidth={2.75} /> Return
                  </span>
                  {STEP_META.map((sm, index) => {
                    const n = index + 1;
                    const isActive = n === step;
                    const done = !isActive && n <= highestStepReached;
                    const reachable = n <= highestStepReached;
                    return (
                      <React.Fragment key={sm.key}>
                      <button
                        type="button"
                        data-step={n}
                        onClick={() => handleStepClick(n)}
                        aria-disabled={!reachable || deskLocked}
                        aria-current={isActive ? "step" : undefined}
                        className={`flex flex-shrink-0 cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40 ${
                          isActive
                            ? "bg-[#3877FC] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_2px_8px_-2px_rgba(56,119,252,0.45)]"
                            : done
                              ? "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                              : "text-slate-500"
                        } ${reachable && !isActive && !deskLocked ? "" : "cursor-default"} ${deskLocked && !isActive ? "opacity-50" : ""}`}
                      >
                        {done ? (
                          <motion.span
                            initial={{ scale: 0.6, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 500, damping: 30, delay: 0.09 }}
                            className="grid h-4 w-4 place-items-center rounded-full bg-emerald-500 text-white"
                          >
                            <CheckIcon className="h-2.5 w-2.5" strokeWidth={4} />
                          </motion.span>
                        ) : (
                          <span className={`grid h-4 w-4 place-items-center rounded-full text-[10px] font-semibold ${
                            isActive ? "bg-white/20 text-white" : "border border-slate-400/70 text-slate-600"
                          }`}>
                            {n}
                          </span>
                        )}
                        {sm.title}
                      </button>
                      {n < STEP_META.length && (
                        <span
                          aria-hidden="true"
                          className={`h-px min-w-[10px] flex-1 rounded-full transition-colors duration-300 ${
                            n < highestStepReached ? "bg-emerald-300" : "bg-slate-200"
                          }`}
                        />
                      )}
                      </React.Fragment>
                    );
                  })}
                </div>
                )}

                {loading ? (
                  <div className="flex flex-col items-center justify-center py-24 text-center" role="status" aria-busy="true">
                    <div className="relative h-12 w-12">
                      <div className="absolute inset-0 animate-spin rounded-full border-2 border-slate-200 border-t-[#3877FC]" />
                      <div className="absolute inset-0 flex items-center justify-center text-[#3877FC]">
                        <PackageIcon className="h-4 w-4" />
                      </div>
                    </div>
                    <p className="mt-4 text-[15.5px] font-semibold text-slate-900">Booking your return…</p>
                    <p className="mt-1 text-[13px] text-slate-500">Allocating the courier and generating the AWB.</p>
                  </div>
                ) : (
                  <FormBody
                    noValidate={step <= 4 ? true : undefined}
                    onSubmit={step <= 4 ? (e) => { e.preventDefault(); handleNext(); } : undefined}
                  >
                    <div ref={stepBodyRef} className={stacked ? "" : "px-5 pb-5 pt-3 lg:px-7"}>
                      {stacked ? (
                        <div>
                          {STEP_META.map((sm, i) => {
                            const n = i + 1;
                            const groups = STEP_GROUPS[i] || [];
                            const complete = groups.length > 0 && groups.every(Boolean);
                            const unlocked = n <= unlockedUpTo && !(deskLocked && n > 1);
                            const current = n === unlockedUpTo;
                            return (
                              <React.Fragment key={sm.key}>
                                <div className="flex flex-col items-center" aria-hidden="true">
                                  {n > 1 && <span className={`h-7 w-px ${n <= unlockedUpTo ? "bg-emerald-300" : "bg-[#D8DFEA]"}`} />}
                                  <span className={`my-2 grid h-8 min-w-[2rem] place-items-center rounded-full px-2 font-mono text-[12px] font-bold tabular-nums ring-4 ring-[#F6F8FB] ${
                                    complete && unlocked ? "bg-emerald-500 text-white"
                                      : current ? "bg-[#3877FC] text-white shadow-[0_2px_8px_-2px_rgba(56,119,252,0.5)]"
                                      : unlocked ? "bg-white text-slate-600 shadow-[inset_0_0_0_1.5px_#C9D3E0]"
                                      : "bg-white text-slate-400 shadow-[inset_0_0_0_1.5px_#E2E8F0]"
                                  }`}>
                                    {complete && unlocked ? <CheckIcon className="h-4 w-4" strokeWidth={3.5} /> : String(n).padStart(2, "0")}
                                  </span>
                                </div>
                                <section
                                  data-section={n}
                                  data-locked={unlocked ? undefined : "true"}
                                  ref={(el) => { if (el) { if (unlocked) el.removeAttribute("inert"); else el.setAttribute("inert", ""); } }}
                                  className={`${CARD} relative scroll-mt-[88px] px-5 pb-5 pt-4 transition-opacity duration-300 lg:px-7 ${unlocked ? "" : "pointer-events-none select-none opacity-55"}`}
                                >
                                  {!unlocked && (
                                    <span className="absolute right-5 top-4 z-10 inline-flex items-center gap-1.5 rounded-full border border-[#E4E9F1] bg-[#F6F8FB] px-2.5 py-1 text-[11px] font-semibold text-slate-500 lg:right-7">
                                      <LockIcon className="h-3 w-3" /> {deskLock ? deskLock.chip : `Complete ${STEP_META[unlockedUpTo - 1].title} first`}
                                    </span>
                                  )}
                                  {renderSectionHeader(n)}
                                  {sm.body()}
                                </section>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      ) : (
                        <AnimatePresence mode="wait" initial={false}>
                          <motion.div
                            key={step}
                            initial={{ opacity: 0, x: stepDir * 32 }}
                            animate={{ opacity: 1, x: 0, transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1], delay: 0.07 } }}
                            exit={{ opacity: 0, x: stepDir * -24, transition: { duration: 0.16, ease: [0.4, 0, 1, 1] } }}
                          >
                            {renderSectionHeader(step)}
                            {STEP_META[step - 1].body()}
                          </motion.div>
                        </AnimatePresence>
                      )}
                    </div>

                    <div ref={footerRef} className={stacked
                      ? `${CARD} mt-7 px-5 py-3 lg:px-7`
                      : "sticky bottom-0 rounded-b-[inherit] border-t border-[#EDF1F7] bg-[#FAFBFD]/95 px-5 py-3 backdrop-blur lg:px-7"}>
                      <AnimatePresence>
                        {moreBelow && !stacked && (
                          <motion.button
                            type="button"
                            onClick={scrollForMore}
                            aria-label="Scroll down for more"
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 6 }}
                            transition={{ duration: 0.2 }}
                            className="absolute -top-[42px] left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-[#E2EAF8] bg-white/95 py-1.5 pl-3 pr-2.5 text-[11px] font-semibold text-[#3877FC] shadow-[0_6px_18px_-6px_rgba(15,23,42,0.28)] backdrop-blur transition-colors hover:bg-white"
                          >
                            More below
                            <motion.span animate={{ y: [0, 2.5, 0] }} transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}>
                              <ChevronDownIcon className="h-3.5 w-3.5" strokeWidth={2.75} />
                            </motion.span>
                          </motion.button>
                        )}
                      </AnimatePresence>
                      <div className="flex flex-wrap items-center gap-3">
                        {step > 1 && !stacked && (
                          <button type="button" onClick={handleBack} className={BTN_GHOST}>
                            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" /> Back
                          </button>
                        )}
                        {liveErrCount > 0 ? (
                          <span className="text-[12.5px] font-semibold text-rose-600" role="alert">
                            {liveErrCount} field{liveErrCount === 1 ? "" : "s"} need{liveErrCount === 1 ? "s" : ""} attention
                          </span>
                        ) : deskLocked ? (
                          <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-700" role="status">
                            <LockIcon className="h-3.5 w-3.5" /> {deskLock.footer}
                          </span>
                        ) : stacked ? (
                          <span className="hidden text-[12px] text-slate-500 md:inline">{unlockedUpTo < 5 ? `Complete ${STEP_META[unlockedUpTo - 1].title} to unlock the next section` : "Every section is open — book when ready"}</span>
                        ) : refStopsHere ? (
                          <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-rose-600" role="status">
                            <LockIcon className="h-3.5 w-3.5" /> Change the order reference to continue.
                          </span>
                        ) : step <= 4 ? (
                          <span className="hidden items-center gap-1.5 text-[12px] text-slate-500 md:flex">
                            <kbd className="rounded-md border-b-2 border-[#C9D3E0] bg-white px-1.5 py-0.5 font-sans text-[11px] font-semibold text-slate-500 ring-1 ring-[#D8DFEA]">↵ Enter</kbd>
                            to continue
                          </span>
                        ) : null}
                        <div className="order-first flex w-full flex-shrink-0 items-center gap-2.5 sm:order-none sm:ml-auto sm:w-auto">
                          {(step === 5 || Boolean(draftOrderId)) && (
                            <button
                              type="button"
                              onClick={handleSaveAsDraft}
                              disabled={loading || savingDraft}
                              className={BTN_OUTLINE}
                            >
                              {savingDraft && <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />}
                              {savingDraft ? "Saving…" : draftOrderId ? "Update draft" : "Save as draft"}
                            </button>
                          )}
                          {step === 5 ? (
                            <button type="button" onClick={stacked ? handleBookStacked : handleSubmit} disabled={loading || savingDraft || deskLocked || refStopsHere || (stacked && unlockedUpTo < 5)} title={deskLock ? deskLock.title : refStopsHere ? "Change the order reference to continue" : stacked && unlockedUpTo < 5 ? `Complete ${STEP_META[unlockedUpTo - 1].title} first` : undefined} className={`${BTN_PRIMARY} flex-1 justify-center sm:flex-none ${stepReady && !deskLocked && !refStopsHere ? "cfo-cta-ready" : ""}`}>
                              Book return <CheckIcon className="h-4 w-4" strokeWidth={3} />
                            </button>
                          ) : (
                            <button type={step <= 4 ? "submit" : "button"} onClick={step <= 4 ? undefined : handleNext} disabled={deskLocked || refStopsHere} title={deskLock ? deskLock.title : refStopsHere ? "Change the order reference to continue" : undefined} className={`${BTN_PRIMARY} flex-1 justify-center sm:flex-none ${stepReady && !deskLocked && !refStopsHere ? "cfo-cta-ready" : ""}`}>
                              Continue <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </FormBody>
                )}
              </motion.div>

              {/* ── The ticket: the return, becoming real — its truck drives home, right to left ── */}
              <motion.aside
                initial={{ x: 16, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: 0.12 }}
                className="mt-6 lg:sticky lg:top-20 lg:mt-0"
              >
                <ShipmentTicket
                  variant="return"
                  pct={pct}
                  step={step}
                  stageLabel={stacked ? `${Math.min(reached - 1, 5)} of 5 done` : undefined}
                  origin={originKnown ? { city: cd.city, pin: cd.pincode } : null}
                  destination={destKnown ? { city: selectedDrop.pickup_city, pin: selectedDrop.pickup_postal_code } : null}
                  rows={ticketRows}
                  cells={ticketCells}
                  orderRef={String(od.clientOrderId || "").trim() || null}
                  charge={chargeValue}
                  settlement={settlementLabel}
                />
                <FormLayoutCard layoutMode={layoutMode} onChoose={chooseLayout} />
              </motion.aside>
            </div>
          )}

          <PickupAddressModal open={addrModalOpen} mode={addrModalMode} location={addrModalLocation} onClose={() => setAddrModalOpen(false)} onSaved={handleAddressSaved} />
          <DeleteConfirmModal open={deleteConfirmOpen} onConfirm={confirmDeleteAddress} onCancel={() => { setDeleteConfirmOpen(false); setPickupToDelete(null); }} deleting={deletingPickup} />

          {/* Saved-customer dialog */}
          {showCustomerModal && ReactDOM.createPortal(
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm animate-fade-in"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cro-saved-customer-title"
              tabIndex={-1}
              onKeyDown={(e) => { if (e.key === "Escape") setShowCustomerModal(false); }}
            >
              <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl animate-scale-in">
                <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 pb-4 pt-5">
                  <div>
                    <h3 id="cro-saved-customer-title" className="text-[16px] font-semibold tracking-[-0.01em] text-slate-900">Saved customer</h3>
                    <p className="mt-0.5 text-[13px] text-slate-500">Choose an address to use it, or close to enter a new one.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCustomerModal(false)}
                    aria-label="Close"
                    className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/50"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="max-h-[60vh] space-y-2.5 overflow-y-auto p-5">
                  {customerLookupResults.map((cust, i) => (
                    <button
                      key={i}
                      type="button"
                      autoFocus={i === 0}
                      onClick={() => selectCustomer(cust)}
                      className="group flex w-full items-start gap-3.5 rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:border-[#3877FC] hover:bg-[#3877FC]/[0.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
                    >
                      <span className="mt-0.5 grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-slate-100 text-[13px] font-semibold text-slate-500 transition-colors group-hover:bg-[#3877FC]/10 group-hover:text-[#2A59BD]">
                        {initialsOf(cust.drop_poc)}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[14px] font-semibold text-slate-900">{cust.drop_poc}</span>
                        <span className="mt-0.5 block text-[12.5px] leading-relaxed text-slate-500 line-clamp-2">{cust.drop_address}</span>
                        <span className="mt-0.5 block text-[12.5px] font-semibold text-slate-700">
                          {[cust.drop_city, cust.drop_state].filter(Boolean).join(", ")} {cust.drop_postal_code}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Draft saved dialog */}
          {draftSuccessModal.open && ReactDOM.createPortal(
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm animate-fade-in" role="dialog" aria-modal="true">
              <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl animate-scale-in">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-emerald-500 text-white shadow-lg shadow-emerald-500/30">
                  <CheckIcon className="h-7 w-7" strokeWidth={2.75} />
                </div>
                <h3 className="mt-4 text-[17px] font-semibold tracking-[-0.01em] text-slate-900">
                  {draftSuccessModal.isUpdate ? "Draft updated" : "Draft saved"}
                </h3>
                {!draftSuccessModal.isUpdate && (
                  <p className="mt-1 text-[13px] text-slate-500">Find it under Draft Orders whenever you're ready.</p>
                )}
                {draftSuccessModal.draftId && (
                  <p className="mx-auto mt-2.5 w-fit rounded-lg bg-slate-100 px-2.5 py-1 font-mono text-[11px] text-slate-500">{draftSuccessModal.draftId}</p>
                )}
                <div className="mt-5 flex flex-col gap-2.5">
                  <button
                    type="button"
                    onClick={() => navigate("/draft-orders")}
                    className={`${BTN_PRIMARY} w-full justify-center`}
                  >
                    Open Draft Orders
                  </button>
                  <div className="flex gap-2.5">
                    <button
                      type="button"
                      onClick={() => navigate("/orders")}
                      className="inline-flex h-10 flex-1 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-[13.5px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
                    >
                      Orders
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDraftSuccessModal({ open: false, draftId: null, isUpdate: false });
                        navigate("/create-reverse-order", { replace: true, state: {} });
                        window.location.reload();
                      }}
                      className="inline-flex h-10 flex-1 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-[13.5px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
                    >
                      New return
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}

          <OrderCreationIssueModal
            issue={orderIssue}
            onClose={() => setOrderIssue(null)}
            onAction={handleOrderIssueAction}
          />
        </div>
      </div>
    </MotionConfig>
  );
};

export default CreateReverseOrder;
